// ticket details 

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Ticket, ArrowLeft, Clock, User, Mail, Phone, Calendar,
  AlertTriangle, CheckCircle, AlertCircle, Paperclip,
  MessageSquare, Play, Pause, Flag, UserCheck,
  ArrowUp, XCircle, Plus, Download, Eye, Edit,
  ChevronDown, ChevronUp, MoreHorizontal, Star,
  FileText, Image, Archive, Trash2, Share2, Copy, X, UserMinus, Sparkles, Check, RefreshCw, RotateCcw, UserPlus, Users, Tag, Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ticketService, isTicketApiSuccess, extractSavedReplyFromResponse } from '../../../api/ticketService';
import { fetchStatuses, fetchPriorities, fetchCategories } from '../../../lib/masterDataQuery';
import { fileService } from '../../../api/fileService';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { formatTicketFileUploadedAt } from '../../../utils/formatTicketFileUploadedAt';
import { formatRequesterDisplay } from '../../../utils/formatRequesterDisplay';
import { canPerformSupervisorApproval } from '../../../utils/notificationNavigation';
import {
  computeSlaCountdown,
  resolveDisplayElapsedMinutes,
  isSlaClockFrozen,
  isSlaPausedStatus,
  isTerminalSlaStatus,
} from '../../../utils/slaCountdown';
import { getUserById } from '../../../api/userManagementService';
import { getUsersWithRole } from '../../../api/roleManagementService';
import { locationService } from '../../../api/locationService';
import { articleService } from '../../../api/articleService';
import { chatbotApiPost } from '../../../services/apiClient';
import { useDisplayTimezone, useTicketRelationsEnabled } from '../../../contexts/AppConfigContext';
import LinkedTicketsPanel from '../../../components/relations/LinkedTicketsPanel';
import LinkedDuplicatesPanel from '../../../components/relations/LinkedDuplicatesPanel';
import AttachExistingTicketModal from '../../../components/relations/AttachExistingTicketModal';
import AttachDuplicateModal from '../../../components/relations/AttachDuplicateModal';
import CloseAsDuplicateModal, {
  canShowCloseAsDuplicateAction,
} from '../../../components/relations/CloseAsDuplicateModal';
import CreateChildTicketModal from '../../../components/relations/CreateChildTicketModal';
import { useFeaturePermissions } from '../../../components/globalfeatures';
import { ConfidentialityLevelBadge } from '../../../components/tickets/QueueConfidentialityLevelControl';
import {
  isManualMatrixEscalationEligible,
  normalizeTicketStatusCode,
} from '../../../utils/manualMatrixEscalationEligibility';
import {
  AUTO_ESCALATE_OUTCOMES,
  AUTO_ESCALATE_RETRY_INTERVAL_MS,
  createInitialAutoEscalateGuard,
  evaluateAutoEscalateAttemptGuard,
  isAutoEscalationFrontendCandidate,
  isCalendarDueDateReached,
  nextDueWakeDelayMs,
  shouldApplyEscalationTicketRefresh,
} from '../../../utils/slaAutoEscalationTrigger';

/** D7 — duplicate ticket cannot be linked when in these status_code values. */
const DUPLICATE_LINK_BLOCKED_STATUS_CODES = new Set([
  'resolved',
  'closed',
  'supervisor_approved',
]);

function formatCustomFieldDisplayValue(value) {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => (item === null || item === undefined ? '' : String(item).trim()))
      .filter((item) => item.length > 0);
    return parts.length > 0 ? parts.join(', ') : '—';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  const text = String(value).trim();
  return text.length > 0 ? text : '—';
}

/** Match a ticket_statuses row by status_code / name (underscores, hyphens, spaces). */
function findTicketStatus(statuses, ...candidates) {
  const list = Array.isArray(statuses) ? statuses : [];
  const wanted = candidates
    .map((c) => String(c || '').toLowerCase().replace(/[\s-]+/g, '_'))
    .filter(Boolean);
  return (
    list.find((s) => {
      const code = String(s.statusCode || s.code || '').toLowerCase().replace(/[\s-]+/g, '_');
      const name = String(s.statusName || s.name || '').toLowerCase().replace(/[\s-]+/g, '_');
      return wanted.some((w) => code === w || name === w || name.includes(w) || code.includes(w));
    }) || null
  );
}

function isHistoricallyReopenedTicket(ticket) {
  return ticket?.isReopened === true;
}

function formatReopenedIndicatorLabel(ticket) {
  if (!isHistoricallyReopenedTicket(ticket)) return null;
  const count = Number(ticket?.reopenCount);
  if (Number.isFinite(count) && count > 0) {
    return count === 1 ? 'Reopened (1 time)' : `Reopened (${count} times)`;
  }
  return 'Reopened';
}

/** Normalize API map / JSON string to a plain object for forms */
function normalizeCustomFieldValues(raw) {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return { ...raw };
  }
  return {};
}

const normalizeTagIdList = (ids) => (
  [...new Set((Array.isArray(ids) ? ids : []).filter((tagId) => tagId != null).map(Number))]
    .filter((tagId) => Number.isFinite(tagId))
    .sort((a, b) => a - b)
);

const tagIdListsEqual = (left, right) => {
  const a = normalizeTagIdList(left);
  const b = normalizeTagIdList(right);
  return a.length === b.length && a.every((value, index) => value === b[index]);
};

/** Normalize /sla-status or ticket SLA fields into one shape for countdown/display. */
const normalizeSlaStatusResponse = (slaData) => {
  if (!slaData) return null;

  return {
    elapsedMinutes: slaData.elapsedMinutes ?? slaData.slaElapsedMinutes ?? null,
    remainingMinutes: slaData.remainingMinutes ?? slaData.slaRemainingMinutes ?? null,
    remainingSeconds: slaData.remainingSeconds ?? slaData.slaRemainingSeconds ?? 0,
    overdueMinutes: slaData.overdueMinutes ?? slaData.slaOverdueMinutes ?? null,
    overdueSeconds: slaData.overdueSeconds ?? slaData.slaOverdueSeconds ?? null,
    isBreached: slaData.isBreached === true || slaData.slaIsBreached === true,
    isEscalated: slaData.isEscalated === true || slaData.slaIsEscalated === true,
    slaApplicable: slaData.slaApplicable === true,
    assignedTime: slaData.assignedTime ?? null,
    slaUsesBusinessHours: slaData.slaUsesBusinessHours === true,
  };
};

/** Map persisted SLA fields from the main ticket API response. */
const mapTicketSlaFields = (ticket) => {
  if (!ticket) return null;

  const hasSlaFields =
    ticket.slaApplicable !== undefined && ticket.slaApplicable !== null
    || ticket.slaRemainingMinutes != null
    || ticket.slaIsBreached != null
    || ticket.slaIsEscalated != null
    || ticket.slaOverdueMinutes != null;

  if (!hasSlaFields && !ticket.assignedTime) {
    return null;
  }

  return {
    elapsedMinutes: ticket.slaElapsedMinutes ?? null,
    remainingMinutes: ticket.slaRemainingMinutes ?? null,
    remainingSeconds: ticket.slaRemainingSeconds ?? 0,
    overdueMinutes: ticket.slaOverdueMinutes ?? 0,
    overdueSeconds: ticket.slaOverdueSeconds ?? 0,
    isBreached: ticket.slaIsBreached === true,
    isEscalated: ticket.slaIsEscalated === true || ticket.escalated === true,
    slaApplicable: ticket.slaApplicable === true,
    assignedTime: ticket.assignedTime ?? null,
  };
};

const TicketDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isViewMode = location.state?.mode === 'view';
  // Backend-configured display timezone (IANA zone id, e.g. "Africa/Nairobi"). Sourced
  // from `app.display-timezone` via /api/ticket/no-auth/config/display-timezone so all
  // browsers render reply timestamps in the same business wall-clock.
  const displayTimezone = useDisplayTimezone();
  // V1 parent-child UI is gated by the backend feature flag — when off, the panel and
  // its modals are not even mounted (zero risk to the existing detail page).
  const ticketRelationsEnabled = useTicketRelationsEnabled();
  const duplicateTicketLinkPerms = useFeaturePermissions('duplicate_ticket_link');
  const manualMatrixEscalationPerms = useFeaturePermissions('manual_inquiry_matrix_escalation');
  const [showManualEscalateModal, setShowManualEscalateModal] = useState(false);
  const [manualEscalateReason, setManualEscalateReason] = useState('');
  const [isManualEscalating, setIsManualEscalating] = useState(false);
  const openManualEscalateHandledRef = useRef(false);
  const [relationsBundle, setRelationsBundle] = useState(null);
  const [relationsLoading, setRelationsLoading] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [showAttachDuplicateModal, setShowAttachDuplicateModal] = useState(false);
  const [showCreateChildModal, setShowCreateChildModal] = useState(false);
  const [showCloseAsDuplicateModal, setShowCloseAsDuplicateModal] = useState(false);
  const [closeAsDuplicateReason, setCloseAsDuplicateReason] = useState('');
  const [closeAsDuplicateError, setCloseAsDuplicateError] = useState('');
  const [closingAsDuplicate, setClosingAsDuplicate] = useState(false);
  const [ticketData, setTicketData] = useState(null);
  const [timeSpent, setTimeSpent] = useState('00:00');
  const [agentTimeSpentList, setAgentTimeSpentList] = useState([]);
  const [loadingTimeSpent, setLoadingTimeSpent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('communications');
  const [showActions, setShowActions] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [isTrackingTime, setIsTrackingTime] = useState(false);
  const [showSolutionPanel, setShowSolutionPanel] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [unassignReason, setUnassignReason] = useState('');
  const [showAssignToTeamLeadsModal, setShowAssignToTeamLeadsModal] = useState(false);
  const [assignToTeamLeadsReason, setAssignToTeamLeadsReason] = useState('');
  /** True while team-lead assign / unassign API runs — prevents double-submit and shows busy UI */
  const [isAssigningToTeamLeads, setIsAssigningToTeamLeads] = useState(false);
  const [isUnassigningTicket, setIsUnassigningTicket] = useState(false);
  const [showAssignInquiryModal, setShowAssignInquiryModal] = useState(false);
  const [assignInquiryUsers, setAssignInquiryUsers] = useState([]);
  const [selectedInquiryAssigneeId, setSelectedInquiryAssigneeId] = useState('');
  const [selectedInquiryAssignPriorityId, setSelectedInquiryAssignPriorityId] = useState('');
  const [inquiryAssignPriorityLevels, setInquiryAssignPriorityLevels] = useState([]);
  const [assignInquiryRoleId, setAssignInquiryRoleId] = useState(null);
  const [assignInquiryRoleName, setAssignInquiryRoleName] = useState('');
  const [assignInquiryEscalationLevelName, setAssignInquiryEscalationLevelName] = useState('');
  const [inquiryAssignmentRemarks, setInquiryAssignmentRemarks] = useState('');
  const [loadingInquiryAssignUsers, setLoadingInquiryAssignUsers] = useState(false);
  const [isAssigningInquiry, setIsAssigningInquiry] = useState(false);
  const [showRequestInfoModal, setShowRequestInfoModal] = useState(false);
  const [requestInfoRemarks, setRequestInfoRemarks] = useState('');
  const [requestInfoFiles, setRequestInfoFiles] = useState([]);
  const [requestInfoError, setRequestInfoError] = useState('');
  const [isSubmittingRequestInfo, setIsSubmittingRequestInfo] = useState(false);
  const [showSupervisorRejectModal, setShowSupervisorRejectModal] = useState(false);
  const [supervisorRejectRemarks, setSupervisorRejectRemarks] = useState('');
  const [supervisorRejectError, setSupervisorRejectError] = useState('');
  const [isSubmittingSupervisorAction, setIsSubmittingSupervisorAction] = useState(false);
  const [solution, setSolution] = useState('');
  const [solutionSummary, setSolutionSummary] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [timeSpentMinutes, setTimeSpentMinutes] = useState('');
  const [resolveFiles, setResolveFiles] = useState([]);
  const [resolveError, setResolveError] = useState('');
  const [isSubmittingResolve, setIsSubmittingResolve] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryApiMessage, setSummaryApiMessage] = useState('');
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [showGenerateArticleModal, setShowGenerateArticleModal] = useState(false);
  const [generatedArticle, setGeneratedArticle] = useState(null);
  const [isGeneratingArticle, setIsGeneratingArticle] = useState(false);
  const [articleApiMessage, setArticleApiMessage] = useState('');
  const [isSavingArticle, setIsSavingArticle] = useState(false);
  const [newReply, setNewReply] = useState('');
  const [replyFormError, setReplyFormError] = useState('');
  const [isAddingReply, setIsAddingReply] = useState(false);
  /** True while POST / reply attachment uploads are in flight — prevents double-submit */
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editReplyContent, setEditReplyContent] = useState('');
  const [replies, setReplies] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadJustCompleted, setUploadJustCompleted] = useState(false);
  const [showDeleteReplyConfirm, setShowDeleteReplyConfirm] = useState(false);
  const [replyToDelete, setReplyToDelete] = useState(null);
  const [showDeleteAttachmentConfirm, setShowDeleteAttachmentConfirm] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [persistedTagIds, setPersistedTagIds] = useState([]);
  const [savingTags, setSavingTags] = useState(false);
  const [replyFiles, setReplyFiles] = useState([]);
  const [slaStatus, setSlaStatus] = useState(null);
  const [slaTimer, setSlaTimer] = useState(null); // Remaining time in minutes
  const [slaTimerSeconds, setSlaTimerSeconds] = useState(null); // Remaining seconds (0-59)
  const [slaOverdueMinutes, setSlaOverdueMinutes] = useState(null); // Overdue minutes (after breach)
  const [slaOverdueSeconds, setSlaOverdueSeconds] = useState(null); // Overdue seconds (0-59)
  const [requesterUser, setRequesterUser] = useState(null);
  const prevStatusRef = useRef(null); // Track previous status for detecting closed -> other state transitions
  const pausedAtRef = useRef(null); // Track when ticket was closed (paused timestamp)
  const totalPausedDurationRef = useRef(0); // Accumulate total paused duration in seconds
  const lastResumeTimeRef = useRef(null); // Track when timer was last resumed (to calculate paused duration)
  const slaStatusFetchedAtRef = useRef(null); // Anchor for continuing overdue tick from API overdueMinutes
  const slaStatusRequestGenRef = useRef(0); // Drop stale overlapping /sla-status responses
  const ticketDataRef = useRef(null);
  ticketDataRef.current = ticketData;
  const autoEscalateGuardRef = useRef(createInitialAutoEscalateGuard());
  const autoEscalateEffectGenRef = useRef(0);
  const ticketLoadGenRef = useRef(0);
  const assignToTeamLeadsSectionRef = useRef(null);
  const unassignSectionRef = useRef(null);
  const resolveSectionRef = useRef(null);
  const [assignedUser, setAssignedUser] = useState(null);
  const [loadingRequester, setLoadingRequester] = useState(false);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [ticketFeedback, setTicketFeedback] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  // Location states
  const [countries, setCountries] = useState([]);
  const [counties, setCounties] = useState([]);
  const [subCounties, setSubCounties] = useState([]);
  const [wards, setWards] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState('');
  const [selectedCountyId, setSelectedCountyId] = useState('');
  const [selectedSubCountyId, setSelectedSubCountyId] = useState('');
  const [selectedWardId, setSelectedWardId] = useState('');
  const [updatingLocation, setUpdatingLocation] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState({
    countries: false,
    counties: false,
    subCounties: false,
    wards: false
  });
  const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
  const normalizedUserRole = String(
    userDetails?.roleCode ||
    userDetails?.roleName ||
    userDetails?.role ||
    userDetails?.userRole ||
    userDetails?.designation ||
    ''
  ).toUpperCase().replace(/[\s-]+/g, '_');
  const isTeamLeadUser = normalizedUserRole === 'TEAM_LEAD';
  const isAdminUser = normalizedUserRole === 'ADMIN' || normalizedUserRole === 'SUPER_ADMIN';
  const canUnassignEscalatedTicket = isTeamLeadUser || isAdminUser;
  const canEditTicketPriority = isTeamLeadUser || isAdminUser;

  const ticketStatusNormalized = String(
    ticketData?.statusCode
    || ticketData?.status?.statusCode
    || ticketData?.status
    || ''
  ).toLowerCase().replace(/[\s-]+/g, '_');
  const isClosedOrResolvedTicket =
    ticketStatusNormalized === 'closed' || ticketStatusNormalized === 'resolved';
  const isAwaitingSupervisorApproval = ticketStatusNormalized === 'pending_supervisor_approval';
  const isSupervisorApprovedStatus = ticketStatusNormalized === 'supervisor_approved';
  const isEscalatedTicket = ticketData?.escalated === true;
  const hasAssignee = ticketData?.assigneeId != null && ticketData?.assigneeId !== '';
  const isInquiryTicket = !!(
    ticketData?.inquiryTypeId
    || ticketData?.inquiryCategoryId
  );
  const canAssignInquiry = isTeamLeadUser && !hasAssignee && isInquiryTicket;
  const sessionUserId = sessionStorage.getItem('userId');
  const isCurrentAssignee =
    hasAssignee
    && sessionUserId
    && String(ticketData?.assigneeId) === String(sessionUserId);
  const canRequestAdditionalInfo =
    isInquiryTicket
    && isCurrentAssignee
    && !isClosedOrResolvedTicket
    && !isAwaitingSupervisorApproval
    && !isSupervisorApprovedStatus
    && !isViewMode;
  const canResolveInquiry = canRequestAdditionalInfo;
  const canCloseAsDuplicate = canShowCloseAsDuplicateAction({
    ticketRelationsEnabled,
    duplicateOf: relationsBundle?.duplicateOf,
    ticket: ticketData,
    allowInteraction: !isViewMode,
  });
  const canApproveInquiry =
    canPerformSupervisorApproval()
    && isInquiryTicket
    && isAwaitingSupervisorApproval
    && !isViewMode;

  const canUnassignTicket =
    hasAssignee
    && !isClosedOrResolvedTicket
    && (!isEscalatedTicket || canUnassignEscalatedTicket);

  const unassignDisabledReason = !hasAssignee
    ? null
    : isClosedOrResolvedTicket
      ? 'Closed or resolved tickets cannot be unassigned'
      : isEscalatedTicket && !canUnassignEscalatedTicket
        ? 'Escalated tickets require Team Lead/Admin reassignment'
        : null;

  const buildLocalSummary = () => {
    const communications = ticketDetail?.communicationHistory || [];
    const commentCount = communications.filter(c => c.type === 'comment').length;
    const noteCount = communications.filter(c => c.type === 'note' || c.type === 'internal').length;
    const totalMessages = communications.length;
    const participants = [...new Set(communications.map(c => c.author || c.user))];
    const timeSpan = communications.length > 0 ? {
      first: communications[communications.length - 1]?.timestamp || communications[communications.length - 1]?.date,
      last: communications[0]?.timestamp || communications[0]?.date
    } : null;

    return {
      overview: `This ticket has ${totalMessages} communication${totalMessages !== 1 ? 's' : ''} involving ${participants.length} participant${participants.length !== 1 ? 's' : ''}. The conversation includes ${commentCount} customer comment${commentCount !== 1 ? 's' : ''} and ${noteCount} internal note${noteCount !== 1 ? 's' : ''}.`,
      keyPoints: [
        communications.length > 0 ? `Initial issue: ${ticketDetail?.description || 'Network connectivity problem reported'}` : 'No communications yet',
        communications.some(c => c.type === 'internal') ? 'Internal investigation and troubleshooting performed by support team' : 'Awaiting internal review',
        communications.some(c => c.message?.toLowerCase().includes('solution') || c.message?.toLowerCase().includes('resolved')) ? 'Solution provided or in progress' : 'Resolution pending',
        `Last activity: ${timeSpan?.last || 'Recently'}`
      ],
      participants,
      sentiment: communications.length > 2 ? 'Collaborative' : 'Initial Contact',
      urgency: ticketDetail?.priority === 'critical' || ticketDetail?.priority === 'high' ? 'High - Requires immediate attention' : 'Normal - Standard response time',
      nextSteps: [
        ticketDetail?.status === 'open' ? 'Continue investigation and provide updates' : 'Monitor for resolution',
        'Keep customer informed of progress',
        noteCount === 0 ? 'Add internal notes for team visibility' : 'Review internal notes for context'
      ],
      timeline: timeSpan ? `Communication span: ${timeSpan.first} to ${timeSpan.last}` : 'New ticket',
      statistics: {
        totalMessages,
        commentCount,
        noteCount,
        participantCount: participants.length
      }
    };
  };

  const fetchTicketData = async () => {
    const loadGen = ++ticketLoadGenRef.current;
    try {
      setLoading(true);
      // Fetch ticket first (required)
      const ticketResponse = await ticketService.getTicketById(id);

      // Backend returns ResponseBean: { status, message, data, success, timestamp }
      // axios response structure: { data: ResponseBean }
      // So ticketResponse.data is the ResponseBean, and ticketResponse.data.data is the actual ticket
      const responseData = ticketResponse?.data;
      let ticket = null;

      if (responseData?.data) {
        // ResponseBean format: extract nested data
        ticket = responseData.data;
      } else if (responseData && !responseData.status && !responseData.success) {
        // Direct ticket object (fallback for mock data)
        ticket = responseData;
      } else if (responseData) {
        // Try responseData itself if it looks like a ticket
        ticket = responseData;
      }

      if (!ticket) {
        setError('Failed to load ticket details');
        toast.error('Failed to load ticket details');
        setLoading(false);
        return;
      }

      // Transform backend fields to frontend format
      const transformedTicket = {
        ...ticket,
        id: ticket.ticketId || ticket.id,
        title: ticket.subject || ticket.title,
        customFieldValues: normalizeCustomFieldValues(ticket.customFieldValues),
      };

      if (loadGen === ticketLoadGenRef.current) {
        setTicketData(transformedTicket);
      }
      const assignedTagIds = normalizeTagIdList(
        Array.isArray(ticket.tags) ? ticket.tags.map((t) => t.tagId) : []
      );
      setSelectedTagIds(assignedTagIds);
      setPersistedTagIds(assignedTagIds);

      // Fetch user details for requester and assigned user (prefer API requesterFullName / email — matches DB user for email-originated tickets)
      const requesterId = ticket.requesterId || ticket.createdBy;
      const assigneeId = ticket.assigneeId || ticket.assignedTo;

      if (ticket.requesterFullName) {
        setRequesterUser({
          name: formatRequesterDisplay(ticket),
          email: ticket.requesterEmail || '',
          username: null
        });
      } else if (requesterId) {
        setLoadingRequester(true);
        try {
          const requesterResponse = await getUserById(requesterId);
          const requesterData = requesterResponse?.data?.data || requesterResponse?.data;
          if (requesterData) {
            setRequesterUser({
              name: requesterData.fullName || `${requesterData.firstName || ''} ${requesterData.lastName || ''}`.trim() || requesterData.username || requesterData.email || 'Unknown User',
              email: requesterData.email,
              username: requesterData.username
            });
          }
        } catch (error) {
          console.error('Error fetching requester user:', error);
        } finally {
          setLoadingRequester(false);
        }
      } else if (ticket.requesterEmail) {
        setRequesterUser({
          name: formatRequesterDisplay(ticket),
          email: ticket.requesterEmail,
          username: null
        });
      }

      if (assigneeId) {
        setLoadingAssigned(true);
        try {
          const assignedResponse = await getUserById(assigneeId);
          const assignedData = assignedResponse?.data?.data || assignedResponse?.data;
          if (assignedData) {
            setAssignedUser({
              name: assignedData.fullName || `${assignedData.firstName || ''} ${assignedData.lastName || ''}`.trim() || assignedData.username || assignedData.email || 'Unknown User',
              email: assignedData.email,
              username: assignedData.username
            });
          }
        } catch (error) {
          console.error('Error fetching assigned user:', error);
          // Don't show error to user, just log it
        } finally {
          setLoadingAssigned(false);
        }
      }

      // Fetch other data in parallel (non-critical)
      const [repliesResponse, attachmentsResponse, statusesResponse, prioritiesResponse, categoriesResponse, tagsResponse] = await Promise.allSettled([
        ticketService.getReplies(id),
        ticketService.getAttachments(id),
        fetchStatuses(),
        fetchPriorities(),
        fetchCategories(),
        ticketService.getTicketTags()
      ]);

      // Fetch feedback if ticket is closed
      // Check status in multiple ways: status field, statusCode, or status object
      const ticketStatus = ticket.status?.toLowerCase() ||
        ticket.statusCode?.toLowerCase() ||
        ticket.status?.statusCode?.toLowerCase() ||
        ticket.status?.statusName?.toLowerCase() || '';

      if (ticketStatus === 'closed') {
        try {
          setLoadingFeedback(true);
          // Use numeric ticketId - prefer ticket.ticketId, fallback to id from URL params
          const ticketIdToFetch = ticket.ticketId || id;
          //console.log('Initial fetch: Fetching feedback for ticket:', ticketIdToFetch, 'Status:', ticketStatus, 'Full ticket:', ticket);

          const feedbackResponse = await ticketService.getFeedback(ticketIdToFetch);
          //console.log('Initial fetch: Feedback API response:', feedbackResponse);

          // Handle different response structures
          if (feedbackResponse?.data?.success && feedbackResponse?.data?.data) {
            setTicketFeedback(feedbackResponse.data.data);
            //console.log('Initial fetch: Feedback loaded:', feedbackResponse.data.data);
          } else if (feedbackResponse?.data?.data === null) {
            // Backend returned null - no feedback exists
            //console.log('Initial fetch: No feedback found (null response)');
            setTicketFeedback(null);
          } else {
            //console.log('Initial fetch: Unexpected feedback response structure:', feedbackResponse);
            setTicketFeedback(null);
          }
        } catch (error) {
          // Log error details for debugging
          console.error('Initial fetch: Error fetching feedback:', error);
          console.error('Initial fetch: Error response:', error.response?.data);
          console.error('Initial fetch: Error message:', error.message);
          setTicketFeedback(null);
        } finally {
          setLoadingFeedback(false);
        }
      } else {
        setTicketFeedback(null);
      }

      // Handle replies
      if (repliesResponse.status === 'fulfilled') {
        const repliesData = repliesResponse.value?.data;
        // Check if it's ResponseBean format (nested data.data)
        if (repliesData?.data) {
          const repliesArray = Array.isArray(repliesData.data) ? repliesData.data : [];
          // Transform backend response to frontend format
          const transformedReplies = repliesArray.map(reply => ({
            id: reply.replyId || reply.id,
            replyId: reply.replyId || reply.id,
            content: reply.content || reply.message || '',
            message: reply.content || reply.message || '',
            createdBy: reply.userName ? { name: reply.userName } : (reply.createdBy || { name: 'System' }),
            author: reply.userName || (reply.author?.name || reply.author || 'System'),
            createdAt: reply.createdAt || reply.timestamp || new Date().toISOString(),
            timestamp: reply.createdAt || reply.timestamp || new Date().toISOString(),
            isInternal: reply.isInternal || false,
            replyType: reply.replyType || reply.type || 'comment',
            type: (reply.replyType === 'internal_note' || reply.replyType === 'unassignment' || reply.replyType === 'team_lead_assignment' || reply.replyType === 'assignment') ? 'internal' : (reply.replyType || 'comment')
          }));
          setReplies(transformedReplies);
        } else if (Array.isArray(repliesData)) {
          // Direct array format (fallback)
          setReplies(repliesData);
        } else {
          setReplies([]);
          console.warn('Replies data format unexpected:', repliesData);
        }
      } else {
        setReplies([]);
        console.warn('Failed to load replies:', repliesResponse.reason);
      }

      // Handle attachments
      if (attachmentsResponse.status === 'fulfilled') {
        const attachmentsData = attachmentsResponse.value?.data;
        // Check if it's ResponseBean format
        if (attachmentsData?.data) {
          setAttachments(Array.isArray(attachmentsData.data) ? attachmentsData.data : []);
        } else if (Array.isArray(attachmentsData)) {
          setAttachments(attachmentsData);
        } else {
          setAttachments([]);
        }
      } else {
        setAttachments([]);
        console.warn('Failed to load attachments:', attachmentsResponse.reason);
      }

      // Handle statuses
      if (statusesResponse.status === 'fulfilled') {
        const statusesData = statusesResponse.value?.data?.data || statusesResponse.value?.data || [];
        const statusesArray = Array.isArray(statusesData) ? statusesData : [];
        setStatuses(statusesArray);

        // Map statusId to status name/code for display and ensure dropdown shows correct value
        if (statusesArray.length > 0) {
          // Get current ticket data to check statusId or status name
          setTicketData(prev => {
            if (!prev || loadGen !== ticketLoadGenRef.current) return prev;

            // Try to find status by statusId first
            let matchedStatus = null;
            const ticketStatusId = prev.statusId || ticket.statusId;

            if (ticketStatusId) {
              matchedStatus = statusesArray.find(s =>
                s.statusId === ticketStatusId ||
                s.statusId?.toString() === ticketStatusId?.toString() ||
                s.id === ticketStatusId ||
                s.id?.toString() === ticketStatusId?.toString()
              );
            }

            // If not found by ID, try to match by status name
            if (!matchedStatus) {
              const ticketStatus = (prev.status || ticket.status || '').toLowerCase();
              if (ticketStatus) {
                matchedStatus = statusesArray.find(s =>
                  (s.statusName || s.statusCode || '').toLowerCase() === ticketStatus ||
                  (s.name || s.code || '').toLowerCase() === ticketStatus
                );
              }
            }

            if (matchedStatus) {
              // Update ticket data with correct statusId and status name
              return {
                ...prev,
                statusId: matchedStatus.statusId || matchedStatus.id,
                statusCode: matchedStatus.statusCode || matchedStatus.code || prev.statusCode,
                status: (matchedStatus.statusName || matchedStatus.name || prev.status || 'open').toLowerCase(),
                statusName: matchedStatus.statusName || matchedStatus.name || prev.statusName
              };
            }

            return prev;
          });
        }
      } else {
        setStatuses([]);
        console.warn('Failed to load statuses:', statusesResponse.reason);
      }

      // Handle priorities
      if (prioritiesResponse.status === 'fulfilled') {
        const prioritiesData = prioritiesResponse.value?.data?.data || prioritiesResponse.value?.data || [];
        const prioritiesArray = Array.isArray(prioritiesData) ? prioritiesData : [];
        setPriorities(prioritiesArray);

        // Map priorityId to priority name/code for display
        if (prioritiesArray.length > 0) {
          // Get current ticket data to check priorityId
          setTicketData(prev => {
            if (!prev || loadGen !== ticketLoadGenRef.current) return prev;

            const priorityId = prev.priorityId || ticket.priorityId;
            if (priorityId) {
              const priority = prioritiesArray.find(p => p.priorityId === priorityId || p.priorityId?.toString() === priorityId?.toString());
              if (priority) {
                // Update ticket data with priority name/code for display
                const priorityCode = (priority.priorityCode || priority.priorityName || '').toLowerCase();
                return {
                  ...prev,
                  priority: priorityCode,
                  priorityName: priority.priorityName || priority.priorityCode,
                  priorityId: priority.priorityId
                };
              }
            }
            return prev;
          });
        }
      } else {
        setPriorities([]);
        console.warn('Failed to load priorities:', prioritiesResponse.reason);
      }

      // Handle tags
      if (tagsResponse.status === 'fulfilled') {
        const raw = tagsResponse.value?.data?.data ?? tagsResponse.value?.data ?? tagsResponse.value;
        const tagsData = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
        setTags(Array.isArray(tagsData) ? tagsData : []);
      } else {
        setTags([]);
        console.warn('Failed to load tags:', tagsResponse.reason);
      }

      // Fetch agent-wise time spent
      await fetchAgentTimeSpent();

      toast.success(`Ticket ${id} loaded successfully`);
    } catch (err) {
      setError('Error loading ticket details');
      toast.error('Error loading ticket details');
      console.error('Ticket detail error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchTicketData();
    } else {
      setError('No ticket ID provided');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    openManualEscalateHandledRef.current = false;
  }, [id]);

  useEffect(() => {
    if (loading || !ticketData || openManualEscalateHandledRef.current) return;
    if (!location.state?.openManualEscalate) return;
    if (isViewMode) return;
    if (!manualMatrixEscalationPerms.canView) return;
    if (!isManualMatrixEscalationEligible(ticketData)) return;

    openManualEscalateHandledRef.current = true;
    setManualEscalateReason('');
    setShowManualEscalateModal(true);
    navigate(`/agent/tickets/${id}`, {
      replace: true,
      state: { ...location.state, mode: 'edit', openManualEscalate: false },
    });
  }, [
    loading,
    ticketData,
    location.state,
    isViewMode,
    manualMatrixEscalationPerms.canView,
    id,
    navigate,
  ]);

  // Load the parent-child relations bundle whenever the ticket id changes (and only when
  // the V1 feature flag is on). Failures degrade the panel to its empty/loading state but
  // never block the rest of the page — the relations panel is strictly additive.
  const fetchRelationsBundle = React.useCallback(async () => {
    if (!ticketRelationsEnabled) return;
    if (!id) return;
    setRelationsLoading(true);
    try {
      const res = await ticketService.getRelationsBundle(id);
      const apiData = res?.data;
      if (apiData?.success === false) {
        console.warn('[TicketDetail] relations bundle responded success=false:', apiData?.message);
        setRelationsBundle(null);
      } else {
        setRelationsBundle(apiData?.data || null);
      }
    } catch (err) {
      // 404 is expected on backends where the feature is disabled mid-flight; suppress
      // those at debug verbosity and treat the panel as empty so the rest of the page
      // is unaffected.
      const status = err?.response?.status || err?.status;
      if (status === 404) {
        console.debug('[TicketDetail] relations endpoint returned 404; feature likely off');
      } else {
        console.warn('[TicketDetail] failed to load relations bundle:', err?.message || err);
      }
      setRelationsBundle(null);
    } finally {
      setRelationsLoading(false);
    }
  }, [id, ticketRelationsEnabled]);

  useEffect(() => {
    fetchRelationsBundle();
  }, [fetchRelationsBundle]);

  const duplicateLinkMode = relationsBundle?.duplicateOf ? 'pick-canonical' : 'pick-duplicate';

  const isCurrentTicketBlockedAsDuplicate = React.useMemo(() => {
    if (!ticketData) return true;
    return DUPLICATE_LINK_BLOCKED_STATUS_CODES.has(normalizeTicketStatusCode(ticketData));
  }, [ticketData]);

  const canShowDuplicateLinkAction =
    ticketRelationsEnabled &&
    duplicateTicketLinkPerms.canView &&
    !isCurrentTicketBlockedAsDuplicate;

  const handleDuplicateLinked = React.useCallback(async () => {
    toast.success('Duplicate ticket linked successfully');
    await fetchRelationsBundle();
  }, [fetchRelationsBundle]);

  const handleOpenCloseAsDuplicate = React.useCallback(() => {
    setCloseAsDuplicateReason('');
    setCloseAsDuplicateError('');
    setShowCloseAsDuplicateModal(true);
  }, []);

  const handleCloseAsDuplicate = React.useCallback(async () => {
    const trimmed = closeAsDuplicateReason.trim();
    if (!trimmed) {
      setCloseAsDuplicateError('Reason is required');
      return;
    }
    if (!id || closingAsDuplicate) return;

    setCloseAsDuplicateError('');
    setClosingAsDuplicate(true);
    try {
      const response = await ticketService.closeAsDuplicate(id, trimmed);
      const responseData = response?.data;
      if (responseData?.success === false) {
        setCloseAsDuplicateError(extractErrorMessage(responseData, 'Failed to close ticket as duplicate'));
        return;
      }
      const canonicalNumber = relationsBundle?.duplicateOf?.ticketNumber || 'the original ticket';
      toast.success(`Ticket closed as duplicate of ${canonicalNumber}`);
      setShowCloseAsDuplicateModal(false);
      setCloseAsDuplicateReason('');
      await fetchTicketData();
      await fetchRelationsBundle();
    } catch (error) {
      setCloseAsDuplicateError(extractErrorMessage(error, 'Failed to close ticket as duplicate'));
    } finally {
      setClosingAsDuplicate(false);
    }
  }, [
    id,
    closeAsDuplicateReason,
    closingAsDuplicate,
    relationsBundle,
    fetchRelationsBundle,
  ]);

  const handleRelationUnlink = React.useCallback(async (relationId, otherTicketNumber) => {
    if (!id || !relationId) return;
    try {
      const res = await ticketService.unlinkRelation(id, relationId);
      const apiData = res?.data;
      if (apiData?.success === false) {
        toast.error(apiData?.message || 'Unlink failed');
        return;
      }
      toast.success(`Unlinked from ${otherTicketNumber || 'ticket'}`);
      await fetchRelationsBundle();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Unlink failed';
      toast.error(msg);
    }
  }, [id, fetchRelationsBundle]);

  // Refetch feedback when ticketData status changes to closed
  useEffect(() => {
    const fetchFeedbackForClosedTicket = async () => {
      if (!ticketData || !ticketData.id) return;

      // Check status in multiple ways
      const currentStatus = ticketData.status?.toLowerCase() ||
        ticketData.statusCode?.toLowerCase() ||
        ticketData.status?.statusCode?.toLowerCase() ||
        ticketData.status?.statusName?.toLowerCase() || '';

      if (currentStatus === 'closed') {
        try {
          setLoadingFeedback(true);
          const ticketIdToFetch = ticketData.ticketId || ticketData.id;
          //console.log('useEffect: Fetching feedback for closed ticket:', ticketIdToFetch, 'Status:', currentStatus);

          const feedbackResponse = await ticketService.getFeedback(ticketIdToFetch);
          //console.log('useEffect: Feedback API response:', feedbackResponse);

          if (feedbackResponse?.data?.success && feedbackResponse?.data?.data) {
            setTicketFeedback(feedbackResponse.data.data);
            //console.log('useEffect: Feedback loaded successfully:', feedbackResponse.data.data);
          } else if (feedbackResponse?.data?.data === null) {
            //console.log('useEffect: No feedback found (null response)');
            setTicketFeedback(null);
          } else {
            //console.log('useEffect: Unexpected feedback response structure:', feedbackResponse);
            setTicketFeedback(null);
          }
        } catch (error) {
          console.error('useEffect: Error fetching feedback:', error);
          console.error('useEffect: Error response:', error.response?.data);
          setTicketFeedback(null);
        } finally {
          setLoadingFeedback(false);
        }
      } else {
        // Clear feedback if ticket is not closed
        setTicketFeedback(null);
      }
    };

    fetchFeedbackForClosedTicket();
  }, [ticketData?.id, ticketData?.status]);

  const mapApiTicketToDetailState = React.useCallback((ticket, prev) => {
    if (!ticket || typeof ticket !== 'object') {
      return prev;
    }
    return {
      ...(prev || {}),
      ...ticket,
      id: ticket.ticketId || ticket.id || prev?.id,
      title: ticket.subject || ticket.title || prev?.title,
      customFieldValues: normalizeCustomFieldValues(
        ticket.customFieldValues ?? prev?.customFieldValues
      ),
    };
  }, []);

  const applyFreshSlaStatus = (slaData, options = {}) => {
    const normalized = normalizeSlaStatusResponse(slaData);
    if (!normalized) {
      return;
    }

    const useD1 = options.d1BusinessSla === true || normalized.slaUsesBusinessHours === true;

    slaStatusFetchedAtRef.current = Date.now();
    setSlaStatus(normalized);

    if (useD1) {
      const countdown = computeSlaCountdown(normalized, 0, { slaUsesBusinessHours: true });
      setSlaTimer(countdown.remainingMinutes);
      setSlaTimerSeconds(countdown.remainingSeconds);
      setSlaOverdueMinutes(countdown.overdueMinutes);
      setSlaOverdueSeconds(countdown.overdueSeconds);
      return;
    }

    setSlaTimer(null);
    setSlaTimerSeconds(null);
    setSlaOverdueMinutes(null);
    setSlaOverdueSeconds(null);
  };

  const refreshAfterAutoEscalation = React.useCallback(async (effectGen) => {
    const ticketLoadGenAtStart = ticketLoadGenRef.current;
    if (
      !shouldApplyEscalationTicketRefresh(
        ticketLoadGenAtStart,
        ticketLoadGenRef.current,
        effectGen,
        autoEscalateEffectGenRef.current
      )
    ) {
      return;
    }

    try {
      const ticketResponse = await ticketService.getTicketById(id);
      if (
        !shouldApplyEscalationTicketRefresh(
          ticketLoadGenAtStart,
          ticketLoadGenRef.current,
          effectGen,
          autoEscalateEffectGenRef.current
        )
      ) {
        return;
      }
      const t = ticketResponse?.data?.data ?? ticketResponse?.data;
      if (t && typeof t === 'object') {
        setTicketData((prev) => mapApiTicketToDetailState(t, prev));
      }
    } catch (error) {
      console.error('Failed to refresh ticket after auto-escalation:', error);
      return;
    }

    try {
      const requestGen = ++slaStatusRequestGenRef.current;
      const slaResponse = await ticketService.getSlaStatus(id);
      if (requestGen !== slaStatusRequestGenRef.current) {
        return;
      }
      if (
        !shouldApplyEscalationTicketRefresh(
          ticketLoadGenAtStart,
          ticketLoadGenRef.current,
          effectGen,
          autoEscalateEffectGenRef.current
        )
      ) {
        return;
      }
      applyFreshSlaStatus(slaResponse?.data?.data, {
        d1BusinessSla: ticketDataRef.current?.slaUsesBusinessHours === true,
      });
    } catch (error) {
      console.error('Failed to refresh SLA status after auto-escalation:', error);
    }
  }, [id, mapApiTicketToDetailState]);

  useEffect(() => {
    autoEscalateGuardRef.current = createInitialAutoEscalateGuard();
  }, [id]);

  // Calendar due_date drives Matrix auto-escalation (not D1 business countdown alone).
  useEffect(() => {
    const effectGen = ++autoEscalateEffectGenRef.current;

    const snapshot = ticketDataRef.current;
    if (!id || !snapshot) {
      return undefined;
    }
    if (!isAutoEscalationFrontendCandidate(snapshot, statuses)) {
      return undefined;
    }

    let dueTimeoutId = null;
    let retryIntervalId = null;
    let overdueRetryStarted = false;

    const ensureOverdueRetryLoop = () => {
      if (overdueRetryStarted) {
        return;
      }
      overdueRetryStarted = true;
      retryIntervalId = setInterval(runAttempt, AUTO_ESCALATE_RETRY_INTERVAL_MS);
    };

    const runAttempt = async () => {
      if (effectGen !== autoEscalateEffectGenRef.current) {
        return;
      }

      const current = ticketDataRef.current;
      if (!isAutoEscalationFrontendCandidate(current, statuses)) {
        return;
      }
      if (!isCalendarDueDateReached(current?.dueDate)) {
        return;
      }

      ensureOverdueRetryLoop();

      const guard = autoEscalateGuardRef.current;
      const { canAttempt, attemptKey } = evaluateAutoEscalateAttemptGuard(
        guard,
        id,
        current.dueDate
      );
      if (!canAttempt) {
        return;
      }

      guard.inFlight = true;
      guard.attemptKey = attemptKey;
      guard.lastAttemptAt = Date.now();

      try {
        const response = await ticketService.tryAutoEscalateSlaIfDue(id);
        if (effectGen !== autoEscalateEffectGenRef.current) {
          return;
        }

        if (!isTicketApiSuccess(response)) {
          console.warn('Auto-escalation attempt rejected:', response?.data?.message);
          return;
        }

        const payload = response?.data?.data;
        const outcome = payload?.outcome;
        if (
          outcome === AUTO_ESCALATE_OUTCOMES.ESCALATED
          && payload?.escalated === true
        ) {
          await refreshAfterAutoEscalation(effectGen);
        }
      } catch (error) {
        console.error('Auto-escalation request failed:', error);
      } finally {
        guard.inFlight = false;
      }
    };

    const scheduleDueWake = () => {
      if (effectGen !== autoEscalateEffectGenRef.current) {
        return;
      }

      const current = ticketDataRef.current;
      if (!isAutoEscalationFrontendCandidate(current, statuses)) {
        return;
      }

      const delay = nextDueWakeDelayMs(current?.dueDate);
      if (delay == null) {
        return;
      }

      if (dueTimeoutId != null) {
        clearTimeout(dueTimeoutId);
        dueTimeoutId = null;
      }

      if (delay <= 0) {
        ensureOverdueRetryLoop();
        runAttempt();
        return;
      }

      dueTimeoutId = setTimeout(() => {
        if (effectGen !== autoEscalateEffectGenRef.current) {
          return;
        }
        const dueNow = ticketDataRef.current;
        if (
          isAutoEscalationFrontendCandidate(dueNow, statuses)
          && isCalendarDueDateReached(dueNow?.dueDate)
        ) {
          ensureOverdueRetryLoop();
          runAttempt();
        }
        scheduleDueWake();
      }, delay);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (effectGen !== autoEscalateEffectGenRef.current) {
        return;
      }
      scheduleDueWake();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    scheduleDueWake();

    return () => {
      if (dueTimeoutId != null) {
        clearTimeout(dueTimeoutId);
      }
      if (retryIntervalId != null) {
        clearInterval(retryIntervalId);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [
    id,
    ticketData?.dueDate,
    ticketData?.assignedTime,
    ticketData?.slaApplicable,
    ticketData?.assigneeId,
    ticketData?.statusCode,
    ticketData?.status,
    statuses,
    refreshAfterAutoEscalation,
  ]);

  // Track status changes to detect closed -> other state transitions and manage paused duration
  useEffect(() => {
    if (ticketData?.status) {
      const slaTicket = ticketData;
      const previousStatus = prevStatusRef.current;

      // Handle status change to closed/resolved - freeze timer display
      if (
        !isTerminalSlaStatus(previousStatus, statuses)
        && isTerminalSlaStatus(slaTicket, statuses)
      ) {
        pausedAtRef.current = new Date();
        const terminalSla = mapTicketSlaFields(ticketData);
        if (terminalSla) {
          setSlaStatus(terminalSla);
          setSlaOverdueMinutes(terminalSla.overdueMinutes ?? null);
          setSlaOverdueSeconds(terminalSla.overdueSeconds ?? null);
        }
        setSlaTimer(null);
        setSlaTimerSeconds(null);
      }

      // Handle status change from closed/resolved to active - restart live SLA tracking
      if (
        isTerminalSlaStatus(previousStatus, statuses)
        && !isTerminalSlaStatus(slaTicket, statuses)
        && ticketData?.assignedTime
      ) {
        // Clear paused duration tracking since we're restarting from the beginning
        // The backend should reset assignedTime when status changes from closed to active
        totalPausedDurationRef.current = 0;
        pausedAtRef.current = null;
        lastResumeTimeRef.current = new Date();

        // Re-fetch SLA status to get fresh timer based on current priority
        // Backend should reset assignedTime, so elapsed time should be 0 or very small
        const fetchSlaStatus = async () => {
          try {
            const requestGen = ++slaStatusRequestGenRef.current;
            const response = await ticketService.getSlaStatus(id);
            if (requestGen !== slaStatusRequestGenRef.current) {
              return;
            }
            applyFreshSlaStatus(response?.data?.data, {
              d1BusinessSla: ticketData?.slaUsesBusinessHours === true,
            });
          } catch (error) {
            console.error('Failed to fetch SLA status after status change:', error);
          }
        };
        fetchSlaStatus();
      }

      // Update previous status reference
      prevStatusRef.current = ticketData.status;
    }
  }, [ticketData?.status, ticketData?.statusCode, ticketData?.statusId, id, ticketData?.assignedTime, statuses]);

  // Fetch SLA status when ticket is first loaded (not on every status change)
  // Status change from closed->active is handled by the status tracking useEffect above
  useEffect(() => {
    if (!id || !ticketData?.assignedTime) {
      setSlaStatus(null);
      setSlaTimer(null);
      setSlaTimerSeconds(null);
      setSlaOverdueMinutes(null);
      setSlaOverdueSeconds(null);
      // Reset paused duration when ticket is unassigned
      totalPausedDurationRef.current = 0;
      pausedAtRef.current = null;
      lastResumeTimeRef.current = null;
      return; // Don't fetch if ticket is not assigned
    }

    // Terminal tickets use persisted SLA fields from the ticket response (stable after reload)
    if (isTerminalSlaStatus(ticketData, statuses)) {
      const terminalSla = mapTicketSlaFields(ticketData);
      setSlaStatus(terminalSla);
      setSlaTimer(null);
      setSlaTimerSeconds(null);
      setSlaOverdueMinutes(terminalSla?.overdueMinutes ?? null);
      setSlaOverdueSeconds(terminalSla?.overdueSeconds ?? null);
      return;
    }

    // Only fetch on initial load (when id or assignedTime changes, but not on status changes)
    // Status changes from closed/resolved->active are handled separately
    const fetchSlaStatus = async () => {
      try {
        const requestGen = ++slaStatusRequestGenRef.current;
        const response = await ticketService.getSlaStatus(id);
        if (requestGen !== slaStatusRequestGenRef.current) {
          return;
        }
        const slaData = response?.data?.data;
        if (slaData) {
          applyFreshSlaStatus(slaData, {
            d1BusinessSla: ticketData?.slaUsesBusinessHours === true,
          });

          // Reset paused duration only on initial load (not when resuming from closed)
          // When resuming from closed, the status tracking useEffect handles it
          if (!prevStatusRef.current || prevStatusRef.current.toLowerCase() !== 'closed') {
            totalPausedDurationRef.current = 0;
            pausedAtRef.current = null;
            lastResumeTimeRef.current = new Date();
          }
        }
      } catch (error) {
        console.error('Failed to fetch SLA status:', error);
        // Don't show error to user, just log it
      }
    };

    // Re-fetch when assignment, applicability, status, or level window changes — not when
    // ticket GET refreshes live slaRemainingMinutes / breach fields (those re-anchor the countdown).
    fetchSlaStatus();
  }, [
    id,
    ticketData?.assignedTime,
    ticketData?.status,
    ticketData?.slaApplicable,
    // A new dueDate means auto-escalation moved the ticket to the next level: re-seed the
    // timer from that level's SLA window instead of continuing the previous level's count.
    ticketData?.dueDate,
    ticketData?.statusCode,
    statuses,
  ]);

  // D1 business SLA: poll /sla-status so remaining time stays in sync with backend working minutes.
  useEffect(() => {
    const D1_SLA_POLL_MS = 60_000;

    if (
      !id
      || ticketData?.slaUsesBusinessHours !== true
      || !ticketData?.assignedTime
      || !slaStatus?.slaApplicable
      || isTerminalSlaStatus(ticketData, statuses)
      || isSlaClockFrozen(ticketData, statuses)
    ) {
      return;
    }

    const refreshD1SlaStatus = async () => {
      try {
        const requestGen = ++slaStatusRequestGenRef.current;
        const response = await ticketService.getSlaStatus(id);
        if (requestGen !== slaStatusRequestGenRef.current) {
          return;
        }
        applyFreshSlaStatus(response?.data?.data, { d1BusinessSla: true });
      } catch (error) {
        console.error('Failed to refresh D1 SLA status:', error);
      }
    };

    // Initial seed comes from the SLA fetch effect above; interval only keeps business-hours in sync.
    const interval = setInterval(refreshD1SlaStatus, D1_SLA_POLL_MS);
    return () => clearInterval(interval);
  }, [
    id,
    ticketData?.slaUsesBusinessHours,
    ticketData?.assignedTime,
    ticketData?.status,
    ticketData?.statusCode,
    slaStatus?.slaApplicable,
    statuses,
  ]);

  // Frontend SLA timer: remaining/overdue seeded from API, then tick every second
  useEffect(() => {
    // Stop timer if ticket is closed or resolved
    if (isTerminalSlaStatus(ticketData, statuses)) {
      setSlaTimer(null);
      setSlaTimerSeconds(null);
      setSlaOverdueMinutes(null);
      setSlaOverdueSeconds(null);
      return () => { };
    }

    const d1BusinessSla = ticketData?.slaUsesBusinessHours === true;
    if (
      (!ticketData?.assignedTime && !d1BusinessSla)
      || !slaStatus?.slaApplicable
      || slaStatus.remainingMinutes === null
      || slaStatus.remainingMinutes === undefined
    ) {
      return;
    }

    const applyCountdown = (tickSeconds) => {
      const countdown = computeSlaCountdown(slaStatus, tickSeconds, {
        slaUsesBusinessHours: d1BusinessSla,
      });
      setSlaTimer(countdown.remainingMinutes);
      setSlaTimerSeconds(countdown.remainingSeconds);
      setSlaOverdueMinutes(countdown.overdueMinutes);
      setSlaOverdueSeconds(countdown.overdueSeconds);
    };

    // Pending customer (stable statusCode): backend froze the clock at sla_paused_at — never tick.
    if (isSlaPausedStatus(ticketData, statuses)) {
      applyCountdown(0);
      return () => { };
    }

    const updateTimer = () => {
      if (isSlaClockFrozen(ticketData, statuses) || !ticketData?.assignedTime) {
        return;
      }

      const anchorMs = slaStatusFetchedAtRef.current ?? Date.now();
      applyCountdown(Math.floor((Date.now() - anchorMs) / 1000));
    };

    // Calculate immediately
    updateTimer();

    // Update every 1 second for live countdown with seconds
    // This avoids hitting the API repeatedly while showing real-time countdown
    const interval = setInterval(updateTimer, 1000);

    // Explicitly clear interval when component unmounts or dependencies change
    return () => {
      clearInterval(interval);
    };
  }, [ticketData, ticketData?.assignedTime, ticketData?.status, ticketData?.statusCode, slaStatus, statuses]);

  // Load locations when ticket or location fields change (not on every status update)
  useEffect(() => {
    if (!ticketData?.id) return;
    setSelectedCountryId(ticketData.countryId || '125');
    setSelectedCountyId(ticketData.countyId || '');
    setSelectedSubCountyId(ticketData.subCountyId || '');
    setSelectedWardId(ticketData.wardId || '');
    loadLocations(ticketData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketData?.id, ticketData?.countryId, ticketData?.countyId, ticketData?.subCountyId, ticketData?.wardId]);

  useEffect(() => {
    if (showAssignToTeamLeadsModal && !isTeamLeadUser) {
      assignToTeamLeadsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showAssignToTeamLeadsModal, isTeamLeadUser]);

  useEffect(() => {
    if (showUnassignModal) {
      unassignSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showUnassignModal]);

  useEffect(() => {
    if (showSolutionPanel) {
      resolveSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showSolutionPanel]);

  // Location loading functions
  const loadLocations = async (ticket = null) => {
    // Load countries
    setLoadingLocations(prev => ({ ...prev, countries: true }));
    try {
      const response = await locationService.getActiveCountries();
      let countriesData = [];
      if (response?.data?.data?.data) {
        countriesData = response.data.data.data;
      } else if (response?.data?.data) {
        countriesData = response.data.data;
      } else if (response?.data) {
        countriesData = response.data;
      } else if (Array.isArray(response)) {
        countriesData = response;
      }
      setCountries(Array.isArray(countriesData) ? countriesData : []);
    } catch (error) {
      console.error('Error loading countries:', error);
      setCountries([]);
    } finally {
      setLoadingLocations(prev => ({ ...prev, countries: false }));
    }

    // If ticket has countryId, load counties
    if (ticket?.countryId) {
      await loadCounties(ticket.countryId);
    } else {
      await loadCounties('125');
    }

    // If ticket has countyId, load subCounties
    if (ticket?.countyId) {
      await loadSubCounties(ticket.countyId);
    }

    // If ticket has subCountyId, load wards
    if (ticket?.subCountyId) {
      await loadWards(ticket.subCountyId);
    }
  };

  const loadCounties = async (countryId) => {
    if (!countryId) {
      setCounties([]);
      return;
    }
    setLoadingLocations(prev => ({ ...prev, counties: true }));
    try {
      const response = await locationService.getActiveCounties(countryId);
      let countiesData = [];
      if (response?.data?.data?.data) {
        countiesData = response.data.data.data;
      } else if (response?.data?.data) {
        countiesData = response.data.data;
      } else if (response?.data) {
        countiesData = response.data;
      } else if (Array.isArray(response)) {
        countiesData = response;
      }
      setCounties(Array.isArray(countiesData) ? countiesData : []);
    } catch (error) {
      console.error('Error loading counties:', error);
      setCounties([]);
    } finally {
      setLoadingLocations(prev => ({ ...prev, counties: false }));
    }
  };

  const loadSubCounties = async (countyId) => {
    if (!countyId) {
      setSubCounties([]);
      return;
    }
    setLoadingLocations(prev => ({ ...prev, subCounties: true }));
    try {
      const response = await locationService.getActiveSubCounties(countyId);
      let subCountiesData = [];
      if (response?.data?.data?.data) {
        subCountiesData = response.data.data.data;
      } else if (response?.data?.data) {
        subCountiesData = response.data.data;
      } else if (response?.data) {
        subCountiesData = response.data;
      } else if (Array.isArray(response)) {
        subCountiesData = response;
      }
      setSubCounties(Array.isArray(subCountiesData) ? subCountiesData : []);
    } catch (error) {
      console.error('Error loading sub counties:', error);
      setSubCounties([]);
    } finally {
      setLoadingLocations(prev => ({ ...prev, subCounties: false }));
    }
  };

  const loadWards = async (subCountyId) => {
    if (!subCountyId) {
      setWards([]);
      return;
    }
    setLoadingLocations(prev => ({ ...prev, wards: true }));
    try {
      const response = await locationService.getActiveWards(subCountyId);
      let wardsData = [];
      if (response?.data?.data?.data) {
        wardsData = response.data.data.data;
      } else if (response?.data?.data) {
        wardsData = response.data.data;
      } else if (response?.data) {
        wardsData = response.data;
      } else if (Array.isArray(response)) {
        wardsData = response;
      }
      setWards(Array.isArray(wardsData) ? wardsData : []);
    } catch (error) {
      console.error('Error loading wards:', error);
      setWards([]);
    } finally {
      setLoadingLocations(prev => ({ ...prev, wards: false }));
    }
  };

  // Handle location changes
  const handleCountryChange = async (e) => {
    const countryId = e.target.value;
    setSelectedCountryId(countryId);
    setSelectedCountyId('');
    setSelectedSubCountyId('');
    setSelectedWardId('');
    setCounties([]);
    setSubCounties([]);
    setWards([]);
    if (countryId) {
      await loadCounties(parseInt(countryId));
    }
  };

  const handleCountyChange = async (e) => {
    const countyId = e.target.value;
    setSelectedCountyId(countyId);
    setSelectedSubCountyId('');
    setSelectedWardId('');
    setSubCounties([]);
    setWards([]);
    if (countyId) {
      await loadSubCounties(parseInt(countyId));
    }
  };

  const handleSubCountyChange = async (e) => {
    const subCountyId = e.target.value;
    setSelectedSubCountyId(subCountyId);
    setSelectedWardId('');
    setWards([]);
    if (subCountyId) {
      await loadWards(parseInt(subCountyId));
    }
  };

  const handleWardChange = (e) => {
    setSelectedWardId(e.target.value);
  };

  const toggleTagSelection = (tagId) => {
    const normalizedId = Number(tagId);
    if (!Number.isFinite(normalizedId)) return;
    setSelectedTagIds((prev) => {
      const current = normalizeTagIdList(prev);
      return current.includes(normalizedId)
        ? current.filter((id) => id !== normalizedId)
        : [...current, normalizedId];
    });
  };

  const handleSaveTags = async () => {
    if (!id || isViewMode) return;
    const uniqueIds = normalizeTagIdList(selectedTagIds);
    if (tagIdListsEqual(uniqueIds, persistedTagIds)) {
      return;
    }
    setSavingTags(true);
    try {
      const response = await ticketService.updateTicket(id, { tagIds: uniqueIds });
      if (!isTicketApiSuccess(response)) {
        toast.error(extractErrorMessage(response, 'Unable to update tags'));
        return;
      }
      const updated = response?.data?.data ?? response?.data ?? response;
      const nextTags = Array.isArray(updated?.tags) ? updated.tags : [];
      const nextTagIds = nextTags.length > 0
        ? normalizeTagIdList(nextTags.map((t) => t.tagId))
        : uniqueIds;
      setTicketData((prev) => (prev ? { ...prev, tags: nextTags } : prev));
      setSelectedTagIds(nextTagIds);
      setPersistedTagIds(nextTagIds);
      toast.success('Tags updated');
    } catch (error) {
      console.error('Error updating tags:', error);
      toast.error(extractErrorMessage(error, 'Unable to update tags'));
    } finally {
      setSavingTags(false);
    }
  };

  // Handle location update
  const handleLocationUpdate = async () => {
    if (!id) return;

    setUpdatingLocation(true);
    try {
      const updateData = {};
      if (selectedCountryId) updateData.countryId = parseInt(selectedCountryId);
      if (selectedCountyId) updateData.countyId = parseInt(selectedCountyId);
      if (selectedSubCountyId) updateData.subCountyId = parseInt(selectedSubCountyId);
      if (selectedWardId) updateData.wardId = parseInt(selectedWardId);

      const response = await ticketService.updateTicket(id, updateData);

      if (response?.data?.success || response?.data?.status === 200) {
        toast.success('Ticket location updated successfully');

        // Find location names
        const country = countries.find(c => (c.id || c.countryId)?.toString() === selectedCountryId?.toString());
        const county = counties.find(c => (c.id || c.countyId)?.toString() === selectedCountyId?.toString());
        const subCounty = subCounties.find(sc => (sc.id || sc.subCountyId)?.toString() === selectedSubCountyId?.toString());
        const ward = wards.find(w => (w.id || w.wardId)?.toString() === selectedWardId?.toString());

        // Update ticket data with new location
        setTicketData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            countryId: selectedCountryId ? parseInt(selectedCountryId) : '125',
            countyId: selectedCountyId ? parseInt(selectedCountyId) : null,
            subCountyId: selectedSubCountyId ? parseInt(selectedSubCountyId) : null,
            wardId: selectedWardId ? parseInt(selectedWardId) : null,
            countryName: country?.name || country?.countryName || null,
            countyName: county?.name || county?.countyName || null,
            subCountyName: subCounty?.name || subCounty?.subCountyName || null,
            wardName: ward?.name || ward?.wardName || null
          };
        });
      } else {
        toast.error(response?.data?.message || 'Failed to update ticket location');
      }
    } catch (error) {
      console.error('Error updating ticket location:', error);
      toast.error(extractErrorMessage(error, 'Failed to update ticket location'));
    } finally {
      setUpdatingLocation(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleAction = (actionId) => {
    const actionMap = {
      'resolve': () => {
        toast.success('Ticket resolved successfully');
        navigate('/agent/tickets/resolution', { state: { ticketId: id } });
      },
      'escalate': () => {
        const eligible =
          manualMatrixEscalationPerms.canView
          && !isViewMode
          && isManualMatrixEscalationEligible(ticketData);
        if (eligible) {
          setShowManualEscalateModal(true);
        } else {
          toast.error('Manual Matrix escalation is not available for this ticket');
        }
      },
      'close': () => {
        toast.success('Ticket closed successfully');
        navigate('/agent/tickets');
      },
      'assign': () => {
        navigate('/agent/tickets/assignment', { state: { ticketId: id } });
      },
      'flag': () => {
        toast.success('Ticket flagged for attention');
      },
      'start-time': () => {
        setIsTrackingTime(true);
        toast.success('Time tracking started');
      }
    };

    if (actionMap[actionId]) {
      actionMap[actionId]();
    } else {
      toast.success(`Action "${actionId}" performed successfully`);
    }
    setShowActions(false);
  };

  const handleAddNote = async () => {
    const trimmed = newNote.trim();
    if (!trimmed) {
      toast.error('Please enter a note');
      return;
    }
    if (trimmed.length < 5) {
      toast.error('Reply must be between 5 and 10000 characters');
      return;
    }

    try {
      // Add note as a reply with internal flag
      const response = await ticketService.addReply(id, {
        content: trimmed,
        isInternal: isInternalNote
      });

      const reply = extractSavedReplyFromResponse(response);
      if (!reply) {
        toast.error(extractErrorMessage(response?.data, 'Failed to add note'));
        return;
      }

      // Transform reply to frontend format
      const transformedReply = {
          id: reply.replyId || reply.id,
          replyId: reply.replyId || reply.id,
          content: reply.content || reply.message || '',
          message: reply.content || reply.message || '',
          createdBy: reply.userName ? { name: reply.userName } : (reply.createdBy || { name: 'System' }),
          author: reply.userName || (reply.author?.name || reply.author || 'System'),
          createdAt: reply.createdAt || reply.timestamp || new Date().toISOString(),
          timestamp: reply.createdAt || reply.timestamp || new Date().toISOString(),
          isInternal: reply.isInternal || false,
          replyType: reply.replyType || reply.type || 'comment',
          type: (reply.replyType === 'internal_note' || reply.replyType === 'unassignment' || reply.replyType === 'team_lead_assignment' || reply.replyType === 'assignment') ? 'internal' : (reply.replyType || 'comment')
        };

        setReplies(prev => {
          const prevArray = Array.isArray(prev) ? prev : [];
          return [transformedReply, ...prevArray];
        });
        setNewNote('');
        setIsAddingNote(false);
        setIsInternalNote(false);
        const noteType = isInternalNote ? 'Internal Note' : 'Note';
        toast.success(`${noteType} added successfully`);
    } catch (error) {
      console.error('Add note error:', error);
      toast.error(extractErrorMessage(error, 'Something went wrong'));
    }
  };

  const resetResolveForm = () => {
    setSolution('');
    setSolutionSummary('');
    setRootCause('');
    setAdditionalNotes('');
    setTimeSpentMinutes('');
    setResolveFiles([]);
    setResolveError('');
  };

  const handleSubmitSolution = async () => {
    if (!canResolveInquiry) {
      toast.error('Only the assigned Responsible User can resolve this inquiry');
      return;
    }
    const remarks = solution.trim();
    if (!remarks) {
      setResolveError('Resolution remarks are required');
      return;
    }
    if (remarks.length < 10 || remarks.length > 10000) {
      setResolveError('Resolution remarks must be between 10 and 10000 characters');
      return;
    }
    if (isSubmittingResolve) return;

    setResolveError('');
    setIsSubmittingResolve(true);
    try {
      const parsedMinutes = timeSpentMinutes === '' ? null : Number(timeSpentMinutes);
      const resolutionData = {
        resolutionContent: remarks,
        solutionSummary: solutionSummary.trim() || null,
        rootCause: rootCause.trim() || null,
        additionalNotes: additionalNotes.trim() || null,
        timeSpentMinutes:
          parsedMinutes != null && Number.isFinite(parsedMinutes) && parsedMinutes > 0
            ? Math.floor(parsedMinutes)
            : null,
        notifyCustomer: true,
      };

      const response = await ticketService.resolveTicket(id, resolutionData);
      if (!(response?.data?.success || isTicketApiSuccess(response))) {
        setResolveError(extractErrorMessage(response?.data, 'Failed to resolve inquiry'));
        return;
      }

      const ticketId = id || ticketData?.id || ticketData?.ticketId;
      const parsedTicketId = parseInt(ticketId, 10);
      if (resolveFiles.length > 0 && !Number.isNaN(parsedTicketId)) {
        try {
          for (const file of resolveFiles) {
            await fileService.uploadFile(file, {
              reg_id: parsedTicketId,
              fileTypeId: 1,
              isActive: true,
              screenName: 'TICKET',
            });
          }
          toast.success(`${resolveFiles.length} attachment(s) uploaded`);
        } catch (uploadError) {
          console.error('Resolve attachment upload failed:', uploadError);
          toast.error('Inquiry resolved but some attachments failed to upload');
        }
      }

      setShowSolutionPanel(false);
      resetResolveForm();
      toast.success('Inquiry resolved — sent for Supervisor Approval');
      await fetchTicketData();
    } catch (error) {
      console.error('Resolve inquiry error:', error);
      setResolveError(extractErrorMessage(error, 'Failed to resolve inquiry'));
    } finally {
      setIsSubmittingResolve(false);
    }
  };

  const handleSupervisorApprove = async () => {
    if (!canApproveInquiry || isSubmittingSupervisorAction) return;
    setIsSubmittingSupervisorAction(true);
    try {
      const response = await ticketService.approveSupervisorResolution(id);
      if (!isTicketApiSuccess(response)) {
        toast.error(response?.data?.message || 'Failed to approve inquiry');
        return;
      }
      toast.success('Inquiry resolution approved (ready for Grace Period)');
      await fetchTicketData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to approve inquiry'));
    } finally {
      setIsSubmittingSupervisorAction(false);
    }
  };

  const handleSupervisorReject = async () => {
    if (!canApproveInquiry || isSubmittingSupervisorAction) return;
    const remarks = supervisorRejectRemarks.trim();
    if (remarks.length < 3) {
      setSupervisorRejectError('Rejection remarks are required (min 3 characters)');
      return;
    }
    setSupervisorRejectError('');
    setIsSubmittingSupervisorAction(true);
    try {
      const response = await ticketService.rejectSupervisorResolution(id, remarks);
      if (!isTicketApiSuccess(response)) {
        setSupervisorRejectError(response?.data?.message || 'Failed to reject inquiry');
        return;
      }
      toast.success('Inquiry rejected — returned to Responsible User');
      setShowSupervisorRejectModal(false);
      setSupervisorRejectRemarks('');
      await fetchTicketData();
    } catch (error) {
      setSupervisorRejectError(extractErrorMessage(error, 'Failed to reject inquiry'));
    } finally {
      setIsSubmittingSupervisorAction(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      // Use the URL from attachment (which includes the domain + document_path)
      if (attachment.url && attachment.url !== '#') {
        // Open in new tab for viewing, or trigger download
        const link = document.createElement('a');
        link.href = attachment.url;
        link.setAttribute('download', attachment.name || attachment.fileName || 'attachment');
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(`Downloading ${attachment.name || attachment.fileName}...`);
      } else {
        toast.error('Attachment URL not available');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download attachment');
    }
  };

  const handleAddReply = async () => {
    const trimmed = newReply.trim();
    if (!trimmed) {
      setReplyFormError('Please enter a reply');
      return;
    }
    if (trimmed.length < 5 || trimmed.length > 10000) {
      setReplyFormError('Reply must be between 5 and 10000 characters');
      return;
    }
    if (isSubmittingReply) {
      return;
    }

    setReplyFormError('');
    setIsSubmittingReply(true);
    try {
      const response = await ticketService.addReply(id, {
        content: trimmed,
        replyChannel: 'web' // Track that reply came from web portal
      });

      const reply = extractSavedReplyFromResponse(response);
      if (!reply) {
        setReplyFormError(extractErrorMessage(response?.data, 'Failed to add reply'));
        return;
      }

      const replyId = reply.replyId || reply.id;

      // Upload attachments if any - use the same file service logic as ticket attachments
      if (replyFiles && replyFiles.length > 0 && replyId) {
          try {
            // Ensure ticket ID is available and valid - use same logic as handleUploadAttachment
            const ticketId = id || ticketData?.id || ticketData?.ticketId;
            if (!ticketId) {
              console.error('Ticket ID not available for reply attachment upload:', { id, ticketData });
              toast.error('Ticket ID not found. Cannot upload attachment.');
              return;
            }

            const parsedTicketId = parseInt(ticketId);
            if (isNaN(parsedTicketId)) {
              console.error('Invalid ticket ID for reply attachment upload:', ticketId);
              toast.error('Invalid ticket ID. Cannot upload attachment.');
              return;
            }

            //console.log('Uploading reply attachments:', {
            //   ticketId: parsedTicketId,
            //   replyId: replyId,
            //   fileCount: replyFiles.length,
            //   screenName: 'TICKET'
            // });

            // Use the same file service API as ticket attachments for consistency
            for (const file of replyFiles) {
              await fileService.uploadFile(file, {
                reg_id: parsedTicketId, // Use ticketId (same as ticket attachments)
                fileTypeId: 1,
                isActive: true,
                screenName: 'TICKET'
              });
            }
            toast.success(`${replyFiles.length} attachment(s) uploaded`);

            // Refresh attachments list to show the newly uploaded files (same as ticket attachments)
            try {
              const attachmentsResponse = await ticketService.getAttachments(parsedTicketId);
              const attachmentsData = attachmentsResponse?.data;
              if (attachmentsData?.data) {
                setAttachments(Array.isArray(attachmentsData.data) ? attachmentsData.data : []);
              } else if (Array.isArray(attachmentsData)) {
                setAttachments(attachmentsData);
              }
            } catch (refreshError) {
              console.warn('Failed to refresh attachments list after reply upload:', refreshError);
              // Don't show error to user as upload was successful
            }
          } catch (uploadError) {
            console.error('Error uploading reply attachments:', uploadError);
            toast.error('Reply added but some attachments failed to upload');
          }
        }

        // Transform reply to frontend format
        const transformedReply = {
          id: reply.replyId || reply.id,
          replyId: reply.replyId || reply.id,
          content: reply.content || reply.message || '',
          message: reply.content || reply.message || '',
          createdBy: reply.userName ? { name: reply.userName } : (reply.createdBy || { name: 'System' }),
          author: reply.userName || (reply.author?.name || reply.author || 'System'),
          createdAt: reply.createdAt || reply.timestamp || new Date().toISOString(),
          timestamp: reply.createdAt || reply.timestamp || new Date().toISOString(),
          isInternal: reply.isInternal || false,
          replyType: reply.replyType || reply.type || 'comment',
          type: (reply.replyType === 'internal_note' || reply.replyType === 'unassignment' || reply.replyType === 'team_lead_assignment' || reply.replyType === 'assignment') ? 'internal' : (reply.replyType || 'comment')
        };

        // Add to replies list
        setReplies(prev => {
          const prevArray = Array.isArray(prev) ? prev : [];
          return [transformedReply, ...prevArray];
        });
        setNewReply('');
        setReplyFiles([]);
        setReplyFormError('');
        setIsAddingReply(false);
        toast.success('Reply added successfully');
    } catch (error) {
      console.error('Add reply error:', error);
      setReplyFormError(extractErrorMessage(error, 'Failed to add reply'));
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleEditReply = async (replyId) => {
    if (!editReplyContent.trim()) {
      toast.error('Please enter reply content');
      return;
    }

    try {
      const response = await ticketService.updateReply(id, replyId, { content: editReplyContent });

      // Extract reply from ResponseBean structure
      const responseData = response?.data;
      const reply = responseData?.data || responseData; // Handle both ResponseBean and direct formats

      if (reply) {
        // Transform reply to frontend format
        const transformedReply = {
          id: reply.replyId || reply.id,
          replyId: reply.replyId || reply.id,
          content: reply.content || reply.message || '',
          message: reply.content || reply.message || '',
          createdBy: reply.userName ? { name: reply.userName } : (reply.createdBy || { name: 'System' }),
          author: reply.userName || (reply.author?.name || reply.author || 'System'),
          createdAt: reply.createdAt || reply.timestamp || new Date().toISOString(),
          timestamp: reply.createdAt || reply.timestamp || new Date().toISOString(),
          isInternal: reply.isInternal || false,
          replyType: reply.replyType || reply.type || 'comment',
          type: (reply.replyType === 'internal_note' || reply.replyType === 'unassignment' || reply.replyType === 'team_lead_assignment' || reply.replyType === 'assignment') ? 'internal' : (reply.replyType || 'comment')
        };

        setReplies(prev => {
          const prevArray = Array.isArray(prev) ? prev : [];
          return prevArray.map(r =>
            r.id === replyId || r.replyId === replyId ? transformedReply : r
          );
        });
        setEditingReplyId(null);
        setEditReplyContent('');
        toast.success('Reply updated successfully');
      }
    } catch (error) {
      console.error('Update reply error:', error);
      toast.error(extractErrorMessage(error, 'Failed to update reply'));
    }
  };

  const handleDeleteReply = async () => {
    if (!replyToDelete) return;

    try {
      await ticketService.deleteReply(id, replyToDelete);
      setReplies(prev => {
        const prevArray = Array.isArray(prev) ? prev : [];
        return prevArray.filter(reply => reply.id !== replyToDelete);
      });
      setShowDeleteReplyConfirm(false);
      setReplyToDelete(null);
      toast.success('Reply deleted successfully');
    } catch (error) {
      console.error('Delete reply error:', error);
      toast.error(extractErrorMessage(error, 'Failed to delete reply'));
    }
  };

  const handleUploadAttachment = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      setUploadingAttachment(true);

      // Ensure ticket ID is available and valid
      const ticketId = id || ticketData?.id || ticketData?.ticketId;
      if (!ticketId) {
        console.error('Ticket ID not available for attachment upload:', { id, ticketData });
        toast.error('Ticket ID not found. Cannot upload attachment.');
        setUploadingAttachment(false);
        return;
      }

      const parsedTicketId = parseInt(ticketId);
      if (isNaN(parsedTicketId)) {
        console.error('Invalid ticket ID for attachment upload:', ticketId);
        toast.error('Invalid ticket ID. Cannot upload attachment.');
        setUploadingAttachment(false);
        return;
      }

      //console.log('Uploading ticket attachment:', {
      //   ticketId: parsedTicketId,
      //   fileName: selectedFile.name,
      //   fileSize: selectedFile.size,
      //   screenName: 'TICKET'
      // });

      // Use the same file service API as CreateTicketForm
      // If no exception is thrown, upload was successful (returns HTTP 202 Accepted)
      await fileService.uploadFile(selectedFile, {
        reg_id: parsedTicketId,
        fileTypeId: 1,
        isActive: true,
        screenName: 'TICKET'
      });

      // Upload successful - refresh attachments list to show the newly uploaded file
      toast.success('Attachment uploaded successfully');
      setSelectedFile(null);
      setUploadJustCompleted(true); // Disable upload button after successful upload
      // Reset file input
      const fileInput = document.getElementById('attachment-file-input');
      if (fileInput) fileInput.value = '';

      // Refresh attachments list to show the newly uploaded file
      try {
        const attachmentsResponse = await ticketService.getAttachments(id);
        const attachmentsData = attachmentsResponse?.data;
        if (attachmentsData?.data) {
          setAttachments(Array.isArray(attachmentsData.data) ? attachmentsData.data : []);
        } else if (Array.isArray(attachmentsData)) {
          setAttachments(attachmentsData);
        }
      } catch (refreshError) {
        console.warn('Failed to refresh attachments list:', refreshError);
        // Don't show error to user as upload was successful
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(extractErrorMessage(error, 'Failed to upload attachment'));
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleDeleteAttachment = async () => {
    if (!attachmentToDelete) return;

    try {
      await ticketService.deleteAttachment(attachmentToDelete);
      setAttachments(prev => prev.filter(att => att.id !== attachmentToDelete));
      setShowDeleteAttachmentConfirm(false);
      setAttachmentToDelete(null);
      toast.success('Attachment deleted successfully');
    } catch (error) {
      console.error('Delete attachment error:', error);
      toast.error(extractErrorMessage(error, 'Failed to delete attachment'));
    }
  };

  const handleUpdateTicket = async () => {
    try {
      const payload = {
        ...editFormData,
        customFieldValues: normalizeCustomFieldValues(editFormData.customFieldValues),
      };
      // Agents cannot change priority via edit modal — omit so update does not 403
      if (!canEditTicketPriority) {
        delete payload.priorityId;
      }
      const response = await ticketService.updateTicket(id, payload);
      if (response?.data?.success) {
        toast.success('Ticket updated successfully');
        setShowEditModal(false);
        const ticketResponse = await ticketService.getTicketById(id);
        const rd = ticketResponse?.data;
        const t = rd?.data ?? rd;
        if (t && typeof t === 'object') {
          setTicketData({
            ...t,
            id: t.ticketId || t.id,
            title: t.subject || t.title,
            customFieldValues: normalizeCustomFieldValues(t.customFieldValues),
          });
        }
      } else {
        toast.error('Failed to update ticket');
      }
    } catch (error) {
      console.error('Update ticket error:', error);
      toast.error(extractErrorMessage(error, 'Failed to update ticket'));
    }
  };

  const handleDeleteTicket = async () => {
    try {
      const response = await ticketService.deleteTicket(id);
      if (response?.data?.success) {
        toast.success('Ticket deleted successfully');
        navigate('/agent/tickets');
      } else {
        toast.error('Failed to delete ticket');
      }
    } catch (error) {
      console.error('Delete ticket error:', error);
      toast.error(extractErrorMessage(error, 'Failed to delete ticket'));
    }
  };

  const formatTimeSpent = (timeString) => {
    if (!timeString) return '0 minutes';

    // Parse time string in format "HH:mm" or "HH:MM:SS"
    const parts = timeString.split(':');
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;

    // Build readable format
    const partsArray = [];
    if (hours > 0) {
      partsArray.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    }
    if (minutes > 0) {
      partsArray.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    }

    // If both are 0, return "0 minutes"
    if (partsArray.length === 0) {
      return '0 minutes';
    }

    return partsArray.join(' ');
  };

  const fetchAgentTimeSpent = async () => {
    if (!id) return;

    try {
      setLoadingTimeSpent(true);
      const response = await ticketService.getTimeSpentByTicketId(id);
      if (response?.data?.data) {
        setAgentTimeSpentList(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching agent time spent:', error);
      // Don't show error toast, just log it
    } finally {
      setLoadingTimeSpent(false);
    }
  };

  const handletimespent = async (timespent) => {

    try {
      //console.log(timespent);
      const response = await ticketService.savetickettimespent(sessionStorage.getItem('userId'), id, timespent);
      // Check if response exists and success is not explicitly false
      // HTTP 200 OK means the request succeeded, even if success field is missing
      if (response?.data && response.data.success !== false) {
        toast.success('Time spent updated successfully');
        // Refresh agent time spent list after saving
        await fetchAgentTimeSpent();
      } else {
        // Only show error if success is explicitly false
        const errorMessage = response?.data?.message || 'Failed to update time spent';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error updating time spent:', error);
      toast.error(error?.response?.data?.message || 'Error updating time spent. Please try again.');
    }
  };

  const handleStatusChange = async (statusId) => {
    try {
      const response = await ticketService.updateTicketStatus(id, statusId);
      if (!response?.data?.success) {
        toast.error('Failed to update status');
        return;
      }

      toast.success('Status updated successfully');

      const newStatusObj = statuses.find(
        (s) => s.statusId === statusId || String(s.statusId) === String(statusId)
      );
      const newStatusName = (newStatusObj?.statusName || newStatusObj?.statusCode || '').toLowerCase();
      const apiTicket = response.data.data;

      setTicketData((prev) => {
        if (!apiTicket) {
          return {
            ...prev,
            statusId,
            statusCode: newStatusObj?.statusCode || newStatusObj?.code || prev?.statusCode,
            status: newStatusName || prev?.status,
          };
        }
        const statusFromApi =
          typeof apiTicket.status === 'string'
            ? apiTicket.status.toLowerCase()
            : (apiTicket.status?.statusCode || apiTicket.status?.statusName || newStatusName || prev?.status || '')
              .toLowerCase();
        return {
          ...prev,
          ...apiTicket,
          id: apiTicket.ticketId || apiTicket.id || prev?.id,
          title: apiTicket.subject || apiTicket.title || prev?.title,
          statusId: apiTicket.statusId ?? statusId,
          statusCode: newStatusObj?.statusCode || newStatusObj?.code || prev?.statusCode,
          status: statusFromApi,
          updatedAt: apiTicket.updatedAt ?? prev?.updatedAt,
          resolvedAt: apiTicket.resolvedAt ?? prev?.resolvedAt,
          closedAt: apiTicket.closedAt ?? prev?.closedAt,
          assignedTime: apiTicket.assignedTime ?? prev?.assignedTime,
          customFieldValues: normalizeCustomFieldValues(
            apiTicket.customFieldValues ?? prev?.customFieldValues
          ),
        };
      });
    } catch (error) {
      console.error('Update status error:', error);
      toast.error(extractErrorMessage(error, 'Failed to update status'));
    }
  };

  const editablePriorities = (priorities || []).filter((p) => {
    const code = (p.priorityCode || '').toLowerCase();
    if (code !== 'not_prioritised') return true;
    // Not Prioritised only visible as current value when ticket is still unassigned
    return !hasAssignee && String(p.priorityId) === String(ticketData?.priorityId);
  });

  const selectedPriorityMaster =
    (priorities || []).find(
      (p) =>
        p.priorityId === ticketData?.priorityId ||
        String(p.priorityId) === String(ticketData?.priorityId)
    ) || null;

  /** Badge-like styling from ticket_priorities.color_code (no hardcoded priority colors). */
  const getPriorityColorCodeStyle = (colorCode) => {
    if (!colorCode || typeof colorCode !== 'string') return undefined;
    const hex = colorCode.startsWith('#') ? colorCode : `#${colorCode}`;
    const bg = hex.length === 7 ? `${hex}33` : hex;
    return {
      backgroundColor: bg,
      color: hex,
      borderColor: hex,
    };
  };

  const handlePriorityChange = async (priorityId) => {
    if (!canEditTicketPriority) {
      toast.error('Only Team Lead or Admin can update priority');
      return;
    }
    if (!priorityId) return;
    if (String(priorityId) === String(ticketData?.priorityId)) return;

    const selected = priorities.find(
      (p) => p.priorityId === priorityId || String(p.priorityId) === String(priorityId)
    );
    const code = (selected?.priorityCode || '').toLowerCase();
    if (hasAssignee && code === 'not_prioritised') {
      toast.error("Not Prioritised cannot be selected once a ticket is assigned");
      return;
    }

    try {
      const response = await ticketService.updateTicketPriority(id, priorityId);
      if (!response?.data?.success) {
        toast.error(response?.data?.message || 'Failed to update priority');
        return;
      }

      toast.success('Priority updated successfully');
      const apiTicket = response.data.data;
      const priorityCode = (
        apiTicket?.priorityCode ||
        selected?.priorityCode ||
        apiTicket?.priorityName ||
        selected?.priorityName ||
        ''
      ).toLowerCase();

      setTicketData((prev) => ({
        ...prev,
        ...(apiTicket || {}),
        id: apiTicket?.ticketId || apiTicket?.id || prev?.id,
        priorityId: apiTicket?.priorityId ?? priorityId,
        priority: priorityCode || prev?.priority,
        priorityName:
          apiTicket?.priorityName ||
          selected?.priorityName ||
          selected?.priorityCode ||
          prev?.priorityName,
        // Keep assignment / SLA clock fields unchanged from prior UI state if API omits them
        assigneeId: prev?.assigneeId,
        assignedTime: prev?.assignedTime,
        assignedAgent: prev?.assignedAgent,
      }));
    } catch (error) {
      console.error('Update priority error:', error);
      toast.error(extractErrorMessage(error, 'Failed to update priority'));
    }
  };

  const openEditModal = () => {
    setEditFormData({
      subject: ticketData?.subject || '',
      description: ticketData?.description || '',
      categoryId: ticketData?.categoryId || null,
      priorityId: ticketData?.priorityId || null,
      statusId: ticketData?.statusId || null,
      assigneeId: ticketData?.assigneeId || null,
      departmentId: ticketData?.departmentId || null,
      teamId: ticketData?.teamId || null,
      customFieldValues: normalizeCustomFieldValues(ticketData?.customFieldValues),
    });
    setShowEditModal(true);
  };

  const handleToggleTimeTracking = () => {
    if (isTrackingTime) {
      toast.success('Time tracking stopped');
      setIsTrackingTime(false);
    } else {
      toast.success('Time tracking started');
      setIsTrackingTime(true);
    }
  };

  const openAssignInquiryModal = async () => {
    setSelectedInquiryAssigneeId('');
    setInquiryAssignmentRemarks('');
    setAssignInquiryUsers([]);
    setSelectedInquiryAssignPriorityId('');
    setAssignInquiryRoleId(null);
    setAssignInquiryRoleName('');
    setAssignInquiryEscalationLevelName('');
    setShowAssignInquiryModal(true);
    try {
      const rows = await ticketService.getInquiryPriorityLevelsForCreate();
      const list = Array.isArray(rows) ? rows : [];
      setInquiryAssignPriorityLevels(
        list.filter((p) => {
          const code = String(p.priorityLevelCode || '').toLowerCase().replace(/[\s-]+/g, '_');
          const name = String(p.priorityLevelName || '').toLowerCase();
          return code !== 'not_prioritised' && name !== 'not prioritised';
        })
      );
    } catch (e) {
      console.error('Failed to load inquiry priority levels', e);
      toast.error(extractErrorMessage(e, 'Failed to load Inquiry Priority Levels'));
      setInquiryAssignPriorityLevels([]);
    }
  };

  const handleInquiryAssignPriorityChange = async (priorityLevelId) => {
    setSelectedInquiryAssignPriorityId(priorityLevelId ? String(priorityLevelId) : '');
    setSelectedInquiryAssigneeId('');
    setAssignInquiryUsers([]);
    setAssignInquiryRoleId(null);
    setAssignInquiryRoleName('');
    setAssignInquiryEscalationLevelName('');
    if (!priorityLevelId) return;

    const categoryId = ticketData?.inquiryCategoryId;
    const subCategoryId = ticketData?.inquirySubCategoryId;
    if (!categoryId || !subCategoryId) {
      toast.error('Inquiry Category / Sub Category missing on this ticket');
      return;
    }
    setLoadingInquiryAssignUsers(true);
    try {
      const role = await ticketService.getLevel1ResponsibleRoleForAssign(
        categoryId,
        subCategoryId,
        Number(priorityLevelId)
      );
      const roleId = role?.responsibleRoleId;
      setAssignInquiryRoleId(roleId || null);
      setAssignInquiryRoleName(role?.responsibleRoleName || '—');
      setAssignInquiryEscalationLevelName(role?.escalationLevelName || 'Level 1');
      if (!roleId) {
        toast.error('No Level 1 Responsible Role found for this Inquiry Priority. Check Escalation Matrix.');
        return;
      }
      const response = await getUsersWithRole(roleId);
      const payload = response?.data?.data ?? response?.data ?? response;
      const users = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.content) ? payload.content : []);
      setAssignInquiryUsers(users);
      if (users.length === 0) {
        toast.error('No users found for the Responsible Role');
      }
    } catch (e) {
      console.error('Failed to resolve Level 1 role for priority', e);
      toast.error(extractErrorMessage(e, 'No Level 1 Responsible Role for this Inquiry Priority'));
      setAssignInquiryUsers([]);
    } finally {
      setLoadingInquiryAssignUsers(false);
    }
  };

  const handleAssignInquiry = async () => {
    if (!selectedInquiryAssignPriorityId) {
      toast.error('Please select an Inquiry Priority Level');
      return;
    }
    if (!selectedInquiryAssigneeId) {
      toast.error('Please select a user');
      return;
    }
    if (!inquiryAssignmentRemarks.trim()) {
      toast.error('Assignment remarks are required');
      return;
    }
    if (isAssigningInquiry) return;

    setIsAssigningInquiry(true);
    try {
      const assignResponse = await ticketService.assignTicket(
        id,
        Number(selectedInquiryAssigneeId),
        null,
        Number(selectedInquiryAssignPriorityId)
      );
      const responseData = assignResponse?.data;
      if (!(responseData?.success || responseData?.status === 200 || isTicketApiSuccess(assignResponse))) {
        toast.error(responseData?.message || 'Failed to assign inquiry');
        return;
      }

      try {
        await ticketService.addReply(id, {
          content: `Assignment remarks: ${inquiryAssignmentRemarks.trim()}`,
          replyType: 'assignment',
          isInternal: true
        });
      } catch (noteErr) {
        console.warn('Assignment succeeded but remarks note failed', noteErr);
      }

      toast.success('Inquiry assigned successfully');
      setShowAssignInquiryModal(false);
      setSelectedInquiryAssigneeId('');
      setSelectedInquiryAssignPriorityId('');
      setInquiryAssignmentRemarks('');
      setAssignInquiryUsers([]);
      await fetchTicketData();
    } catch (error) {
      console.error('Error assigning inquiry:', error);
      if (error?.status === 409) {
        toast.error(error.message || 'This inquiry is already assigned.');
      } else {
        toast.error(extractErrorMessage(error, 'Error assigning inquiry'));
      }
    } finally {
      setIsAssigningInquiry(false);
    }
  };

  const handleRequestAdditionalInfo = async () => {
    if (!canRequestAdditionalInfo) {
      toast.error('Only the assigned Responsible User can request additional information');
      return;
    }
    const remarks = requestInfoRemarks.trim();
    if (!remarks) {
      setRequestInfoError('Remarks are required');
      return;
    }
    if (remarks.length < 5 || remarks.length > 10000) {
      setRequestInfoError('Remarks must be between 5 and 10000 characters');
      return;
    }
    if (isSubmittingRequestInfo) return;

    setRequestInfoError('');
    setIsSubmittingRequestInfo(true);
    try {
      const response = await ticketService.addReply(id, {
        content: remarks,
        replyType: 'additional_info_request',
        isInternal: false,
        replyChannel: 'web',
      });
      const reply = extractSavedReplyFromResponse(response);
      if (!reply) {
        setRequestInfoError(extractErrorMessage(response?.data, 'Failed to request additional information'));
        return;
      }

      const replyId = reply.replyId || reply.id;
      if (requestInfoFiles.length > 0 && replyId) {
        const ticketId = id || ticketData?.id || ticketData?.ticketId;
        const parsedTicketId = parseInt(ticketId, 10);
        if (!Number.isNaN(parsedTicketId)) {
          try {
            for (const file of requestInfoFiles) {
              await fileService.uploadFile(file, {
                reg_id: parsedTicketId,
                fileTypeId: 1,
                isActive: true,
                screenName: 'TICKET',
              });
            }
            const attachmentsResponse = await ticketService.getAttachments(parsedTicketId);
            const attachmentsData = attachmentsResponse?.data;
            if (attachmentsData?.data) {
              setAttachments(Array.isArray(attachmentsData.data) ? attachmentsData.data : []);
            } else if (Array.isArray(attachmentsData)) {
              setAttachments(attachmentsData);
            }
          } catch (uploadError) {
            console.error('Request-info attachment upload failed:', uploadError);
            toast.error('Request sent but some attachments failed to upload');
          }
        }
      }

      const pendingStatus = findTicketStatus(
        statuses,
        'pending_customer',
        'pending-customer',
        'pending_customer_response',
        'awaiting_customer'
      );
      if (pendingStatus?.statusId) {
        await handleStatusChange(pendingStatus.statusId);
      } else {
        toast(
          'Additional information requested. Configure a pending_customer status to reflect waiting-on-requester.',
          { icon: 'ℹ️' }
        );
      }

      const transformedReply = {
        id: reply.replyId || reply.id,
        replyId: reply.replyId || reply.id,
        content: reply.content || '',
        message: reply.content || '',
        createdBy: reply.userName ? { name: reply.userName } : { name: 'System' },
        author: reply.userName || 'System',
        createdAt: reply.createdAt || new Date().toISOString(),
        timestamp: reply.createdAt || new Date().toISOString(),
        isInternal: false,
        replyType: 'additional_info_request',
        type: 'additional_info_request',
      };
      setReplies((prev) => [transformedReply, ...(Array.isArray(prev) ? prev : [])]);
      setShowRequestInfoModal(false);
      setRequestInfoRemarks('');
      setRequestInfoFiles([]);
      toast.success('Additional information requested from requester');
    } catch (error) {
      console.error('Request additional info error:', error);
      setRequestInfoError(extractErrorMessage(error, 'Failed to request additional information'));
    } finally {
      setIsSubmittingRequestInfo(false);
    }
  };

  const handleAssignToTeamLeads = async () => {
    if (!assignToTeamLeadsReason.trim()) {
      toast.error('Please provide a reason for assigning this ticket to team leads');
      return;
    }
    if (isAssigningToTeamLeads) return;

    setIsAssigningToTeamLeads(true);
    try {
      // Prefer session userId (set at login) so this still works if userDetails was cleared or not loaded yet
      const sessionUserId = sessionStorage.getItem('userId');
      const parsedSessionId = sessionUserId ? Number(sessionUserId) : NaN;
      const currentAgentId =
        (Number.isFinite(parsedSessionId) ? parsedSessionId : null) ??
        userDetails?.userId ??
        userDetails?.id ??
        ticketData?.assigneeId ??
        null;

      if (currentAgentId == null || currentAgentId === '') {
        toast.error('Unable to determine agent ID. Please sign in again.');
        return;
      }

      // Call the API to assign to team leads
      const assignResponse = await ticketService.assignToTeamLeads(id, currentAgentId);

      if (isTicketApiSuccess(assignResponse)) {
        // Add an internal note about the team lead assignment with replyType 'team_lead_assignment'
        try {
          const teamLeadNote = `Ticket assigned to team leads. Reason: ${assignToTeamLeadsReason}`;
          await ticketService.addReply(id, {
            content: teamLeadNote,
            isInternal: true,
            replyType: 'team_lead_assignment'
          });
        } catch (noteError) {
          console.warn('Failed to add internal note:', noteError);
          // Don't fail the assignment if note fails
        }

        toast.success('Ticket assigned to team leads successfully');

        // Close the modal
        setShowAssignToTeamLeadsModal(false);
        setAssignToTeamLeadsReason('');

        // Redirect to My Tickets page after successful assignment
        setTimeout(() => {
          navigate('/agent/tickets/my-tickets');
        }, 500); // Small delay to allow toast to be visible
      } else {
        const responseData = assignResponse?.data;
        toast.error(responseData?.message || 'Failed to assign ticket to team leads');
      }
    } catch (error) {
      console.error('Error assigning ticket to team leads:', error);
      toast.error(
        extractErrorMessage(error, 'Error assigning ticket to team leads. Please try again.')
      );
    } finally {
      setIsAssigningToTeamLeads(false);
    }
  };

  const handleManualMatrixEscalate = async () => {
    if (!manualEscalateReason.trim()) {
      toast.error('Please provide a reason for escalation');
      return;
    }
    if (isManualEscalating) return;
    setIsManualEscalating(true);
    try {
      const response = await ticketService.manualInquiryMatrixEscalate(
        id,
        manualEscalateReason.trim()
      );
      if (isTicketApiSuccess(response)) {
        toast.success('Ticket escalated to the next Matrix tier');
        setShowManualEscalateModal(false);
        setManualEscalateReason('');
        setTimeout(() => {
          navigate('/agent/tickets/my-tickets');
        }, 500);
      } else {
        toast.error(response?.data?.message || 'Failed to escalate ticket');
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to escalate ticket'));
    } finally {
      setIsManualEscalating(false);
    }
  };

  const handleUnassignTicket = async () => {
    if (!canUnassignTicket) {
      toast.error(unassignDisabledReason || 'This ticket cannot be unassigned');
      return;
    }
    if (!unassignReason.trim()) {
      toast.error('Please provide a reason for unassigning this ticket');
      return;
    }
    if (isUnassigningTicket) return;

    setIsUnassigningTicket(true);
    try {
      // Unassign the ticket by setting assigneeId to null using updateTicket
      // Send only assigneeId: null to indicate we want to unassign
      const updateData = {
        assigneeId: null
      };

      const updateResponse = await ticketService.updateTicket(id, updateData);

      if (isTicketApiSuccess(updateResponse)) {
        // Add an internal note about the unassignment with replyType 'unassignment'
        try {
          const unassignNote = `Ticket unassigned. Reason: ${unassignReason}`;
          await ticketService.addReply(id, {
            content: unassignNote,
            isInternal: true,
            replyType: 'unassignment'
          });
        } catch (noteError) {
          console.warn('Failed to add internal note:', noteError);
          // Don't fail the unassignment if note fails
        }

        toast.success('Ticket unassigned successfully');

        // Close the modal
        setShowUnassignModal(false);
        setUnassignReason('');

        await fetchTicketData();

        setTimeout(() => {
          if (isTeamLeadUser) {
            navigate('/agent/tickets/team-tickets');
          } else {
            navigate('/agent/tickets/unassigned');
          }
        }, 1000);
      } else {
        const responseData = updateResponse?.data;
        toast.error(responseData?.message || 'Failed to unassign ticket');
      }
    } catch (error) {
      console.error('Error unassigning ticket:', error);
      toast.error(extractErrorMessage(error, 'Error unassigning ticket. Please try again.'));
    } finally {
      setIsUnassigningTicket(false);
    }
  };

  const handleSummarizeCommunications = async () => {
    setIsSummarizing(true);
    setShowSummaryModal(true);
    setSummaryApiMessage('');

    const localSummary = buildLocalSummary();
    const communications = ticketDetail?.communicationHistory || [];

    try {
      const apiKey = process.env.REACT_APP_SUMMARY_API_KEY;
      //console.log(ticketDetail?.userId);
      const payload = {
        // ticket_id: id,
        ticket_number: ticketDetail?.ticketNumber,
        tenant_id: ticketDetail?.tenantId,
        user_id: ticketDetail?.userId

        // ticket: {
        // id: ticketDetail?.id,
        // ticket_number: ticketDetail?.ticketNumber,
        // title: ticketDetail?.title,
        // description: ticketDetail?.description,
        // priority: ticketDetail?.priority,
        // status: ticketDetail?.status,
        // category: ticketDetail?.category
        // },
        // communication_history: communications
      };

      const result = await chatbotApiPost('api/ticket/summary', payload, {
        headers: apiKey ? { 'x-api-key': apiKey } : {},
      });

      const apiSuccessMessage =
        result?.message ||
        result?.summary_message ||
        result?.status_message ||
        'Summary generated successfully from AI service.';
      setSummaryApiMessage(apiSuccessMessage);

      // Map the actual API response fields to the normalized structure
      // The API returns: reply_count, conversation_count, comments_count, internal_note_count, 
      // participants_count, participant_names, summary, key_points
      const normalizedSummary = {
        overview: result.overview || result.summary || localSummary.overview,
        keyPoints: Array.isArray(result.key_points) ? result.key_points :
          (Array.isArray(result.keyPoints) ? result.keyPoints :
            (localSummary.keyPoints || [])),
        participants: Array.isArray(result.participant_names) ? result.participant_names :
          (Array.isArray(result.participants) ? result.participants :
            (localSummary.participants || [])),
        sentiment: result.sentiment || localSummary.sentiment,
        urgency: result.urgency || localSummary.urgency,
        nextSteps: result.nextSteps || result.next_steps || localSummary.nextSteps,
        timeline: result.timeline || localSummary.timeline,
        statistics: {
          // Map direct response fields first, then fallback to nested statistics, then local summary
          totalMessages: result.conversation_count ?? result.reply_count ?? result.statistics?.totalMessages ?? result.statistics?.total_messages ?? communications.length ?? 0,
          commentCount: result.comments_count ?? result.statistics?.commentCount ?? result.statistics?.comment_count ?? localSummary.statistics.commentCount ?? 0,
          noteCount: result.internal_note_count ?? result.statistics?.noteCount ?? result.statistics?.note_count ?? localSummary.statistics.noteCount ?? 0,
          participantCount: result.participants_count ?? result.statistics?.participantCount ?? result.statistics?.participant_count ?? localSummary.statistics.participantCount ?? 0,
          replyCount: result.reply_count ?? 0,
          conversationCount: result.conversation_count ?? 0
        }
      };

      setSummary(normalizedSummary);
      toast.success('Communication history summarized successfully');
    } catch (error) {
      console.error('Summary error:', error);
      setSummary(localSummary);
      if (!summaryApiMessage) {
        const fallbackMessage =
          error?.message ||
          'AI summary service is currently unavailable. Showing quick local summary.';
        setSummaryApiMessage(fallbackMessage);
      }
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleGenerateArticle = async () => {
    setIsGeneratingArticle(true);
    setShowGenerateArticleModal(true);
    setGeneratedArticle(null);
    setArticleApiMessage('');

    try {
      const payload = {
        ticket_id: ticketDetail?.ticketNumber || ticketDetail?.ticket_number || id
      };

      const result = await chatbotApiPost('api/ticket/generate-article', payload);

      if (result.success && result.generated_article) {
        setGeneratedArticle(result.generated_article);
        // Keep error/info container hidden on successful generation
        setArticleApiMessage('');
        toast.success('Knowledge article generated successfully');
      } else {
        const formatMessage =
          result?.message || 'AI service responded, but no article content was returned.';
        setArticleApiMessage(formatMessage);
        throw new Error(formatMessage);
      }
    } catch (error) {
      console.error('Generate Article error:', error);
      setGeneratedArticle(null);
      if (!articleApiMessage) {
        setArticleApiMessage(
          error?.message || 'AI article service is currently unavailable. Please try again.'
        );
      }
    } finally {
      setIsGeneratingArticle(false);
    }
  };

  const handleSaveArticle = async () => {
    if (!generatedArticle) {
      toast.error('No article to save');
      return;
    }

    setIsSavingArticle(true);

    try {
      // Map generated article to API request format
      const articleData = {
        title: generatedArticle.title,
        question: generatedArticle.question,
        answer: generatedArticle.answer,
        category: generatedArticle.category || null,
        tags: generatedArticle.tags || [],
        ticketId: generatedArticle.ticketId || ticketDetail?.ticketNumber || ticketDetail?.ticket_number || null,
        createdBy: userDetails?.fullName || userDetails?.name || userDetails?.username || 'admin',
        status: 'draft', // Default to draft as per requirements
        roleId: userDetails?.roleId || userDetails?.role_id || null,
        tenantId: ticketDetail?.tenantId || userDetails?.tenantId || userDetails?.tenant_id || null
      };

      // Validate required fields
      if (!articleData.title || !articleData.question || !articleData.answer) {
        throw new Error('Article is missing required fields (title, question, or answer)');
      }

      if (!articleData.tenantId) {
        throw new Error('Tenant ID is required to save the article');
      }

      const response = await articleService.createArticle(articleData);

      if (response?.data) {
        toast.success('Knowledge article saved successfully!');
        // Close the modal after successful save
        setShowGenerateArticleModal(false);
        // Optionally clear the generated article state
        setGeneratedArticle(null);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Save Article error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save article';
      toast.error(errorMessage);
    } finally {
      setIsSavingArticle(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900';
      case 'urgent': return 'text-red-50 dark:text-red-100 bg-red-800 dark:bg-red-950';
      case 'high': return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900';
      case 'medium': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900';
      case 'low': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900';
      case 'open': return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900';
      case 'pending': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900';
      case 'resolved': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      case 'closed': return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
      case 'reopen': return 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    }
  };

  const getSLAStatusColor = (slaStatus) => {
    switch (slaStatus) {
      case 'on-track': return 'text-green-600 dark:text-green-400';
      case 'warning': return 'text-yellow-600 dark:text-yellow-400';
      case 'breach': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getSLAIcon = (slaStatus) => {
    switch (slaStatus) {
      case 'on-track': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'breach': return AlertCircle;
      default: return Clock;
    }
  };

  const formatTimeRemaining = (minutes) => {
    if (minutes < 0) {
      return `${Math.abs(minutes)}m overdue`;
    } else if (minutes < 60) {
      return `${minutes}m left`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m left`;
    } else {
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      return `${days}d ${hours}h left`;
    }
  };

  // Format time from minutes to readable string (for elapsed/remaining)
  const formatTimeFromMinutes = (minutes) => {
    if (minutes === null || minutes === undefined) return 'N/A';
    const absMinutes = Math.abs(minutes);
    if (absMinutes < 60) {
      return minutes < 0 ? `${absMinutes}m overdue` : `${absMinutes}m`;
    } else if (absMinutes < 1440) {
      const hours = Math.floor(absMinutes / 60);
      const mins = absMinutes % 60;
      return minutes < 0 ? `${hours}h ${mins}m overdue` : `${hours}h ${mins}m`;
    } else {
      const days = Math.floor(absMinutes / 1440);
      const hours = Math.floor((absMinutes % 1440) / 60);
      return minutes < 0 ? `${days}d ${hours}h overdue` : `${days}d ${hours}h`;
    }
  };

  // Format time with seconds for countdown display
  const formatTimeWithSeconds = (minutes, seconds) => {
    if (minutes === null || minutes === undefined) return 'N/A';
    if (seconds === null || seconds === undefined) seconds = 0;

    const absMinutes = Math.abs(minutes);
    const absSeconds = Math.abs(seconds);

    if (absMinutes < 60) {
      return minutes < 0
        ? `${absMinutes}m ${absSeconds}s overdue`
        : `${absMinutes}m ${absSeconds}s`;
    } else if (absMinutes < 1440) {
      const hours = Math.floor(absMinutes / 60);
      const mins = absMinutes % 60;
      return minutes < 0
        ? `${hours}h ${mins}m ${absSeconds}s overdue`
        : `${hours}h ${mins}m ${absSeconds}s`;
    } else {
      const days = Math.floor(absMinutes / 1440);
      const hours = Math.floor((absMinutes % 1440) / 60);
      const mins = absMinutes % 60;
      return minutes < 0
        ? `${days}d ${hours}h ${mins}m ${absSeconds}s overdue`
        : `${days}d ${hours}h ${mins}m ${absSeconds}s`;
    }
  };

  const formatSlaRemaining = (minutes, seconds) => {
    if (minutes === null || minutes === undefined) return 'N/A';
    const secs = seconds ?? 0;
    if (minutes === 0 && secs === 0) return '0s remaining';
    return `${formatTimeWithSeconds(minutes, secs)} remaining`;
  };

  const formatSlaOverdue = (minutes, seconds) => {
    if (minutes === null || minutes === undefined) return 'N/A';
    const mins = minutes ?? 0;
    const secs = seconds ?? 0;
    if (mins === 0 && secs === 0) return '0s remaining';
    if (mins === 0) return `${secs}s overdue`;
    return formatTimeWithSeconds(-mins, -secs);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading ticket details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!ticketData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">No ticket data available</p>
        </div>
      </div>
    );
  }

  // The ticketData IS the ticket itself (not nested in ticketDetail)
  const ticketDetail = ticketData || {};

  const canShowManualMatrixEscalate =
    manualMatrixEscalationPerms.canView
    && !isViewMode
    && isManualMatrixEscalationEligible(ticketDetail);

  // Mock additional data that might be needed
  const ticketActions = [
    { id: 'resolve', label: 'Resolve', icon: 'check' },
    { id: 'escalate', label: 'Escalate', icon: 'arrow-up' },
    { id: 'close', label: 'Close', icon: 'x' },
    { id: 'assign', label: 'Reassign', icon: 'user' },
    { id: 'flag', label: 'Flag', icon: 'flag' }
  ].filter((action) => action.id !== 'escalate' || canShowManualMatrixEscalate);

  const statusOptions = [
    { value: 'new', label: 'New' },
    { value: 'open', label: 'Open' },
    { value: 'pending', label: 'Pending' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const agents = [
    { id: 'agent_1', name: 'John Doe' },
    { id: 'agent_2', name: 'Jane Smith' }
  ];

  // Additional safety check for required ticket properties
  if (!ticketDetail.id || !ticketDetail.title) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Ticket details are incomplete or invalid</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Ticket ID: {id}</p>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-w-0 bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className=" mx-auto px-0 sm:px-6 lg:px-8">
          <div className="flex h-16 min-w-0 items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center">
              <button
                type="button"
                onClick={handleBack}
                className="shrink-0 p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                title="Go Back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex min-w-0 flex-1 items-center space-x-3">
                <div className="shrink-0 rounded-lg bg-blue-100 p-2 dark:bg-blue-900">
                  <Ticket className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="min-w-0 flex-1 pr-2">
                  <h1 className="truncate text-xl font-semibold text-gray-900 dark:text-gray-100">
                    {ticketDetail.ticketNumber || ticketDetail.ticket_number || ticketDetail.id}
                  </h1>
                </div>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {/* <button
                onClick={handleSummarizeCommunications}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center space-x-2"
                title="AI-powered summary of communication history"
              >
                <Sparkles className="h-4 w-4" />
                <span>Summarize</span>
              </button> */}
              {/* <button
                onClick={handleGenerateArticle}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center space-x-2 ml-2 mr-2"
                title="AI-powered knowledge article generation from ticket"
              >
                <FileText className="h-4 w-4" />
                <span>Generate Article</span>
              </button> */}
              {/* {!isTeamLeadUser && (
                <button
                  type="button"
                  onClick={() => setShowAssignToTeamLeadsModal(true)}
                  disabled={isViewMode || isAssigningToTeamLeads}
                  className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2 mr-2 ${isViewMode || isAssigningToTeamLeads ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Assign ticket to all team leads in your team(s)"
                >
                  {isAssigningToTeamLeads ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4 shrink-0" />
                  )}
                  <span>Assign to Team Leads</span>
                </button>
              )} */}

              {canAssignInquiry && (
                <button
                  type="button"
                  onClick={openAssignInquiryModal}
                  disabled={isAssigningInquiry}
                  className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2 mr-2 ${isAssigningInquiry ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Assign inquiry to a user in the Level 1 Responsible Role"
                >
                  {isAssigningInquiry ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4 shrink-0" />
                  )}
                  <span>Assign Inquiry</span>
                </button>
              )}

              {canRequestAdditionalInfo && (
                <button
                  type="button"
                  onClick={() => {
                    setRequestInfoError('');
                    setRequestInfoRemarks('');
                    setRequestInfoFiles([]);
                    setShowRequestInfoModal(true);
                  }}
                  disabled={isSubmittingRequestInfo}
                  className={`px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 flex items-center space-x-2 mr-2 ${isSubmittingRequestInfo ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Ask the requester for more information"
                >
                  <MessageSquare className="h-4 w-4 shrink-0" />
                  <span>Request Additional Information</span>
                </button>
              )}

              {canShowManualMatrixEscalate && (
                <button
                  type="button"
                  onClick={() => {
                    setManualEscalateReason('');
                    setShowManualEscalateModal(true);
                  }}
                  disabled={isManualEscalating}
                  className={`px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 flex items-center space-x-2 mr-2 ${isManualEscalating ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Advance this inquiry one Matrix escalation tier"
                >
                  {isManualEscalating ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  ) : (
                    <ArrowUp className="h-4 w-4 shrink-0" />
                  )}
                  <span>Escalate</span>
                </button>
              )}

              {canResolveInquiry && (
                <button
                  type="button"
                  onClick={() => {
                    resetResolveForm();
                    setShowSolutionPanel(true);
                    setShowRequestInfoModal(false);
                  }}
                  disabled={isSubmittingResolve}
                  className={`px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2 mr-2 ${isSubmittingResolve ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Resolve this inquiry with resolution remarks"
                >
                  {isSubmittingResolve ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 shrink-0" />
                  )}
                  <span>Resolve Inquiry</span>
                </button>
              )}

              {canApproveInquiry && (
                <>
                  <button
                    type="button"
                    onClick={handleSupervisorApprove}
                    disabled={isSubmittingSupervisorAction}
                    className={`px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2 mr-2 ${isSubmittingSupervisorAction ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Approve resolution (does not close; Grace Period is Phase 7)"
                  >
                    {isSubmittingSupervisorAction ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 shrink-0" />
                    )}
                    <span>Approve Resolution</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSupervisorRejectError('');
                      setSupervisorRejectRemarks('');
                      setShowSupervisorRejectModal(true);
                    }}
                    disabled={isSubmittingSupervisorAction}
                    className={`px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center space-x-2 mr-2 ${isSubmittingSupervisorAction ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Reject resolution and return to Responsible User"
                  >
                    <XCircle className="h-4 w-4 shrink-0" />
                    <span>Reject Resolution</span>
                  </button>
                </>
              )}

              {/* {canUnassignTicket && (
                <button
                  type="button"
                  onClick={() => setShowUnassignModal(true)}
                  disabled={isViewMode || isUnassigningTicket}
                  className={`px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 flex items-center space-x-2 ${isViewMode || isUnassigningTicket ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={
                    isTeamLeadUser
                      ? 'Remove assignee and return ticket to team pending queue'
                      : 'Unassign ticket and return to unassigned tickets page for reassignment'
                  }
                >
                  {isUnassigningTicket ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  ) : (
                    <UserMinus className="h-4 w-4 shrink-0" />
                  )}
                  <span>Unassign</span>
                </button>
              )} */}
              {!canUnassignTicket && unassignDisabledReason && hasAssignee && (
                <span
                  className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-md max-w-xs"
                  title={unassignDisabledReason}
                >
                  {unassignDisabledReason}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="py-8">
        <div className="grid min-w-0 grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left Column - Ticket Info */}
          <div className="min-w-0 max-w-full space-y-6 lg:col-span-2">
            {/* Ticket Information */}
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <div className="mb-4 flex min-w-0 flex-wrap items-start justify-between gap-2">
                <h2 className="min-w-0 text-lg font-semibold text-gray-900 dark:text-gray-100">Ticket Information</h2>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full border ${
                      isInquiryTicket || !(selectedPriorityMaster?.colorCode || selectedPriorityMaster?.color_code)
                        ? getPriorityColor(
                            (isInquiryTicket
                              ? (ticketData?.inquiryPriorityLevelName || ticketData?.priorityName || ticketData?.priority)
                              : (ticketData?.priority || ticketData?.priorityName)
                            )?.toLowerCase() || 'low'
                          )
                        : ''
                    }`}
                    style={
                      isInquiryTicket
                        ? undefined
                        : getPriorityColorCodeStyle(
                            selectedPriorityMaster?.colorCode || selectedPriorityMaster?.color_code
                          )
                    }
                  >
                    {(
                      isInquiryTicket
                        ? (ticketData?.inquiryPriorityLevelName || ticketData?.priorityName || ticketData?.priority)
                        : (ticketData?.priorityName || ticketData?.priority)
                      || 'low'
                    ).toUpperCase()}
                  </span>
                  {isHistoricallyReopenedTicket(ticketData) && (
                    <span className="px-2 py-1 text-xs font-medium rounded-full text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900">
                      {formatReopenedIndicatorLabel(ticketData)}
                    </span>
                  )}
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(ticketData?.status || 'open')}`}>
                    {(ticketData?.status || 'open').toUpperCase()}
                  </span>
                  <ConfidentialityLevelBadge level={ticketData?.confidentialityLevel} />
                </div>
              </div>

              <div className="mb-4 min-w-0 border-b border-gray-200 pb-4 dark:border-gray-700">
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">Subject</label>
                <p className="mt-1 max-w-full whitespace-pre-wrap break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                  {ticketDetail.title?.trim() ? ticketDetail.title : '—'}
                </p>
              </div>

              {/* Tags */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">Tags</label>
                {selectedTagIds.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedTagIds.map((tagId) => {
                      const tag = tags.find((t) => Number(t.tagId) === Number(tagId))
                        || (ticketData?.tags || []).find((t) => Number(t.tagId) === Number(tagId));
                      if (!tag) return null;
                      const isInactiveAssigned = tag.active === false;
                      return (
                        <span
                          key={tagId}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                            isInactiveAssigned ? 'border border-dashed border-gray-400 opacity-80' : ''
                          }`}
                          style={{ backgroundColor: tag.color || '#e5e7eb', color: '#000' }}
                        >
                          {tag.tagName || tag.name}
                          {isInactiveAssigned ? ' (inactive)' : ''}
                          {!isViewMode && isInactiveAssigned && (
                            <button
                              type="button"
                              onClick={() => toggleTagSelection(tagId)}
                              disabled={savingTags}
                              className="ml-0.5 rounded-full hover:bg-black/10 disabled:opacity-50"
                              title="Remove inactive tag"
                              aria-label={`Remove inactive tag ${tag.tagName || tag.name}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                )}
                {!isViewMode && (
                  <div className="space-y-3">
                    {tags.length === 0 ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {selectedTagIds.length > 0
                          ? 'No active tags available to add. Remove assigned tags above or save to clear.'
                          : 'No predefined tags available for this tenant.'}
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {tags.map((tag) => {
                          const tagId = tag.tagId;
                          const selected = normalizeTagIdList(selectedTagIds).includes(Number(tagId));
                          return (
                            <button
                              key={tagId}
                              type="button"
                              onClick={() => toggleTagSelection(tagId)}
                              disabled={savingTags}
                              className={`px-2 py-1 text-xs font-medium rounded-full border transition-colors ${
                                selected
                                  ? 'border-primary bg-primary/10 text-primary'
                                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                              } ${savingTags ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary'}`}
                            >
                              {tag.tagName || tag.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleSaveTags}
                      disabled={savingTags || tagIdListsEqual(selectedTagIds, persistedTagIds)}
                      className={`px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 ${
                        savingTags || tagIdListsEqual(selectedTagIds, persistedTagIds) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {savingTags ? 'Saving tags…' : 'Save tags'}
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="min-w-0">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Inquiry Type</label>
                  <p className="break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                    {ticketDetail.inquiryTypeName || 'N/A'}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Inquiry Category</label>
                  <p className="break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                    {ticketDetail.inquiryCategoryName || 'N/A'}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Inquiry Sub Category</label>
                  <p className="break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                    {ticketDetail.inquirySubCategoryName || 'N/A'}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Inquiry Priority Level</label>
                  <p className="break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                    {ticketDetail.inquiryPriorityLevelName || 'N/A'}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Responsible Role</label>
                  <p className="break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                    {ticketDetail.responsibleRoleName || 'N/A'}
                    {ticketDetail.escalationLevelName ? ` (${ticketDetail.escalationLevelName})` : ''}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Assigned User</label>
                  <p className="break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                    {loadingAssigned
                      ? 'Loading...'
                      : (assignedUser?.name || ticketDetail.assigneeFullName || 'Unassigned')}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Assignment Remarks</label>
                  <p className="break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                    {(() => {
                      const note = (replies || []).find((r) =>
                        r.replyType === 'assignment'
                        || (typeof r.content === 'string' && r.content.startsWith('Assignment remarks:'))
                      );
                      if (!note) return 'N/A';
                      const text = note.content || note.message || '';
                      return text.replace(/^Assignment remarks:\s*/i, '') || 'N/A';
                    })()}
                  </p>
                </div>
                <div className="min-w-0">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Category</label>
                  <p className="break-words text-sm capitalize text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                    {ticketDetail.inquiryCategoryName || ticketDetail.category || 'N/A'}
                  </p>
                </div>
                {/* <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Department</label>
                  <p className="text-sm text-gray-900 dark:text-gray-100 capitalize">{ticketDetail.department || 'N/A'}</p>
                </div> */}
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Created</label>
                  <p className="text-sm text-gray-900 dark:text-gray-100">
                    {new Date(ticketDetail.createdAt).toLocaleString()}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Last Updated</label>
                  <p className="text-sm text-gray-900 dark:text-gray-100">
                    {new Date(ticketDetail.updatedAt).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="mb-4 min-w-0">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Description</label>
                <p className="mt-1 max-w-full whitespace-pre-wrap break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                  {ticketDetail.description}
                </p>
              </div>

              {ticketDetail.customFieldValues &&
                typeof ticketDetail.customFieldValues === 'object' &&
                Object.keys(ticketDetail.customFieldValues).length > 0 && (
                  <div className="mb-4 min-w-0 border-t border-gray-200 pt-4 dark:border-gray-700">
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                      Custom fields
                    </label>
                    <dl className="space-y-2">
                      {Object.entries(ticketDetail.customFieldValues).map(([fieldCode, fieldValue]) => (
                        <div key={fieldCode} className="flex min-w-0 flex-wrap items-start gap-2">
                          <dt className="shrink-0 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {fieldCode.replace(/_/g, ' ')}:
                          </dt>
                          <dd className="min-w-0 flex-1 text-sm text-gray-900 [overflow-wrap:anywhere] whitespace-pre-wrap break-words dark:text-gray-100">
                            {formatCustomFieldDisplayValue(fieldValue)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

              <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-2">
                <div className="flex min-w-0 max-w-full items-center gap-2">
                  <User className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="shrink-0 text-sm text-gray-600 dark:text-gray-400">Requester:</span>
                  <span className="min-w-0 break-words text-sm font-medium text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                    {loadingRequester ? (
                      <span className="text-gray-400">Loading...</span>
                    ) : (
                      requesterUser?.name
                      || formatRequesterDisplay(ticketDetail)
                      || ticketDetail.requester?.name || ticketDetail.createdBy || 'Unknown'
                    )}
                  </span>
                </div>
                <div className="flex min-w-0 max-w-full items-center gap-2">
                  <UserCheck className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="shrink-0 text-sm text-gray-600 dark:text-gray-400">Assigned:</span>
                  <span className="min-w-0 break-words text-sm font-medium text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
                    {loadingAssigned ? (
                      <span className="text-gray-400">Loading...</span>
                    ) : (
                      assignedUser?.name || ticketDetail.assignedAgent?.name || ticketDetail.assignedTo || 'Unassigned'
                    )}
                  </span>
                </div>
              </div>

              {/* Location Information with Edit */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 block">Location</label>
                <div className="grid grid-cols-2 gap-3">
                  {/* Country Dropdown */}
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Country *</label>
                    <select
                      className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${updatingLocation || loadingLocations.countries || isViewMode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      value={selectedCountryId || ''}
                      onChange={handleCountryChange}
                      disabled={updatingLocation || loadingLocations.countries || isViewMode}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option
                          key={country.id || country.countryId}
                          value={country.id || country.countryId}
                        >
                          {country.name || country.countryName || `Country ${country.id || country.countryId}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* County Dropdown */}
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">County</label>
                    {selectedCountryId ? (
                      <select
                        className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${updatingLocation || loadingLocations.counties || isViewMode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        value={selectedCountyId || ''}
                        onChange={handleCountyChange}
                        disabled={updatingLocation || loadingLocations.counties || isViewMode}
                      >
                        <option value="">Select</option>
                        {counties.map((county) => (
                          <option
                            key={county.id || county.countyId}
                            value={county.id || county.countyId}
                          >
                            {county.name || county.countyName || `County ${county.id || county.countyId}`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        disabled
                      >
                        <option value="">Select</option>
                      </select>
                    )}
                  </div>

                  {/* Sub County Dropdown */}
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Sub County</label>
                    {selectedCountyId ? (
                      <select
                        className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${updatingLocation || loadingLocations.subCounties || isViewMode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        value={selectedSubCountyId || ''}
                        onChange={handleSubCountyChange}
                        disabled={updatingLocation || loadingLocations.subCounties || isViewMode}
                      >
                        <option value="">Select</option>
                        {subCounties.map((subCounty) => (
                          <option
                            key={subCounty.id || subCounty.subCountyId}
                            value={subCounty.id || subCounty.subCountyId}
                          >
                            {subCounty.name || subCounty.subCountyName || `Sub County ${subCounty.id || subCounty.subCountyId}`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        disabled
                      >
                        <option value="">Select</option>
                      </select>
                    )}
                  </div>

                  {/* Ward Dropdown */}
                  {/* <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Ward</label>
                    {selectedSubCountyId ? (
                      <select
                        className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${updatingLocation || loadingLocations.wards || isViewMode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        value={selectedWardId || ''}
                        onChange={handleWardChange}
                        disabled={updatingLocation || loadingLocations.wards || isViewMode}
                      >
                        <option value="">Select</option>
                        {wards.map((ward) => (
                          <option 
                            key={ward.id || ward.wardId} 
                            value={ward.id || ward.wardId}
                          >
                            {ward.name || ward.wardName || `Ward ${ward.id || ward.wardId}`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        disabled
                      >
                        <option value="">Select</option>
                      </select>
                    )}
                  </div> */}

                  {/* Update Location Button - Span full width */}
                  {(selectedCountryId || selectedCountyId || selectedSubCountyId || selectedWardId) && !isViewMode && (
                    <div className="col-span-2">
                      <button
                        onClick={handleLocationUpdate}
                        disabled={updatingLocation || isViewMode}
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {updatingLocation ? (
                          <>
                            <RefreshCw className="h-4 w-4 animate-spin" />
                            <span>Updating...</span>
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4" />
                            <span>Update Location</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Display current location if exists but no selection made */}
                  {!selectedCountryId && !selectedCountyId && !selectedSubCountyId && !selectedWardId &&
                    (ticketDetail.countryName || ticketDetail.countyName || ticketDetail.subCountyName || ticketDetail.wardName) && (
                      <div className="col-span-2 text-sm text-gray-600 dark:text-gray-400">
                        Current: {[
                          ticketDetail.countryName,
                          ticketDetail.countyName,
                          ticketDetail.subCountyName,
                          ticketDetail.wardName
                        ].filter(Boolean).join(' > ') || 'No location set'}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Resolve Inquiry Panel */}
            {showSolutionPanel && (
              <div ref={resolveSectionRef} className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900 dark:to-emerald-900 rounded-lg border-2 border-green-300 dark:border-green-700 p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-600 rounded-lg">
                      <CheckCircle className="h-6 w-6 text-white" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Resolve Inquiry</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (isSubmittingResolve) return;
                      setShowSolutionPanel(false);
                      resetResolveForm();
                    }}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Resolution Remarks <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={solution}
                      onChange={(e) => {
                        setSolution(e.target.value);
                        if (resolveError) setResolveError('');
                      }}
                      placeholder="Describe how this inquiry was resolved (visible to the requester)..."
                      disabled={isViewMode || isSubmittingResolve}
                      className={`w-full p-3 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-green-500 ${
                        resolveError
                          ? 'border-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      } ${isViewMode || isSubmittingResolve ? 'opacity-50 cursor-not-allowed' : ''}`}
                      rows={5}
                    />
                    {resolveError && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{resolveError}</p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Minimum 10 characters. Shared with the requester in the conversation.
                    </p>
                  </div>


                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Attachments (Optional)
                    </label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => setResolveFiles(Array.from(e.target.files || []))}
                      disabled={isViewMode || isSubmittingResolve}
                      className={`w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 ${isViewMode || isSubmittingResolve ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    {resolveFiles.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {resolveFiles.map((file, idx) => (
                          <li key={`${file.name}-${idx}`} className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                            <span className="flex min-w-0 items-center gap-2">
                              <Paperclip className="h-4 w-4 shrink-0" />
                              <span className="truncate">{file.name}</span>
                            </span>
                            <button
                              type="button"
                              disabled={isSubmittingResolve}
                              onClick={() => setResolveFiles((prev) => prev.filter((_, i) => i !== idx))}
                              className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-md p-4">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Before submitting:</p>
                        <ul className="text-xs text-blue-700 dark:text-blue-300 mt-1 space-y-1 ml-4 list-disc">
                          <li>Confirm the inquiry is fully addressed</li>
                          <li>Resolution remarks will appear in the requester conversation</li>
                          <li>Status will change to Resolved</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end space-x-3 pt-4 border-t border-green-200 dark:border-green-700">
                    <button
                      type="button"
                      onClick={() => {
                        setShowSolutionPanel(false);
                        resetResolveForm();
                      }}
                      disabled={isViewMode || isSubmittingResolve}
                      className={`px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md ${isViewMode || isSubmittingResolve ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmitSolution}
                      disabled={isViewMode || isSubmittingResolve || !solution.trim()}
                      className={`px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2 font-medium min-w-[12rem] justify-center ${isViewMode || isSubmittingResolve || !solution.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isSubmittingResolve ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin shrink-0" />
                          <span>Resolving…</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-5 w-5 shrink-0" />
                          <span>Submit & Resolve</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* C3 — Manual Inquiry Matrix escalation */}
            {showManualEscalateModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Escalate to next Matrix tier
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        if (isManualEscalating) return;
                        setShowManualEscalateModal(false);
                        setManualEscalateReason('');
                      }}
                      disabled={isManualEscalating}
                      className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                    Advances this Inquiry one escalation level using the configured Matrix. Assignment
                    follows the same capacity rules as automatic escalation.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Reason for escalation <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={manualEscalateReason}
                        onChange={(e) => setManualEscalateReason(e.target.value)}
                        placeholder="Explain why this ticket must move to the next escalation tier..."
                        disabled={isViewMode || isManualEscalating}
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:ring-2 focus:ring-amber-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                        rows={4}
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowManualEscalateModal(false);
                          setManualEscalateReason('');
                        }}
                        disabled={isManualEscalating}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 dark:border-gray-600 dark:text-gray-400"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleManualMatrixEscalate}
                        disabled={isViewMode || isManualEscalating || !manualEscalateReason.trim()}
                        className="flex min-w-[12rem] items-center justify-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isManualEscalating ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Escalating…
                          </>
                        ) : (
                          <>
                            <ArrowUp className="h-4 w-4" />
                            Confirm escalation
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Assign to Team Leads Modal */}
            {!isTeamLeadUser && showAssignToTeamLeadsModal && (
              <div ref={assignToTeamLeadsSectionRef} className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900 rounded-lg border-2 border-blue-300 dark:border-blue-700 p-6 shadow-lg mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-600 rounded-lg">
                      <UserPlus className="h-6 w-6 text-white" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Assign to Team Leads</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAssignToTeamLeadsModal(false);
                      setAssignToTeamLeadsReason('');
                    }}
                    disabled={isAssigningToTeamLeads}
                    className={`p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 ${isAssigningToTeamLeads ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-md p-4">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100">About Assigning to Team Leads</p>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                          When you assign this ticket to team leads:
                        </p>
                        <ul className="text-xs text-blue-700 dark:text-blue-300 mt-2 space-y-1 ml-4 list-disc">
                          <li>The ticket will be visible to all team leads in your team(s)</li>
                          <li>Any team lead can assign the ticket to themselves</li>
                          <li>An internal note will be added for tracking purposes</li>
                          <li>The ticket will be unassigned until a team lead claims it</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Reason for Escalation to Team Leads *
                    </label>
                    <textarea
                      value={assignToTeamLeadsReason}
                      onChange={(e) => setAssignToTeamLeadsReason(e.target.value)}
                      placeholder="E.g., 'This ticket requires team lead approval' or 'Complex issue that needs team lead expertise' or 'Customer escalation request'..."
                      disabled={isViewMode}
                      className={`w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                      rows={4}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      This reason will be added as an internal note to help team leads understand the context
                    </p>
                  </div>

                  <div className="bg-amber-50 dark:bg-amber-900 border border-amber-200 dark:border-amber-700 rounded-md p-3">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          <strong>Current Assignment:</strong> {ticketDetail.assignedAgent?.name || userDetails.fullName || ticketDetail.assignedTo || 'agent_1'}<br />
                          <strong>Department:</strong> {ticketDetail.department || userDetails.departmentName || 'IT'}<br />
                          <strong>Category:</strong> {ticketDetail.category || userDetails.category || 'Access'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end space-x-3 pt-4 border-t border-blue-200 dark:border-blue-700">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAssignToTeamLeadsModal(false);
                        setAssignToTeamLeadsReason('');
                      }}
                      disabled={isViewMode || isAssigningToTeamLeads}
                      className={`px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md ${isViewMode || isAssigningToTeamLeads ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAssignToTeamLeads}
                      disabled={isViewMode || isAssigningToTeamLeads}
                      className={`px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2 font-medium min-w-[12rem] justify-center ${isViewMode || isAssigningToTeamLeads ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isAssigningToTeamLeads ? (
                        <Loader2 className="h-5 w-5 shrink-0 animate-spin" />
                      ) : (
                        <UserPlus className="h-5 w-5 shrink-0" />
                      )}
                      <span>{isAssigningToTeamLeads ? 'Submitting…' : 'Assign to Team Leads'}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Unassign Modal */}
            {showUnassignModal && (
              <div ref={unassignSectionRef} className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900 dark:to-amber-900 rounded-lg border-2 border-orange-300 dark:border-orange-700 p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-orange-600 rounded-lg">
                      <UserMinus className="h-6 w-6 text-white" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Unassign Ticket</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowUnassignModal(false);
                      setUnassignReason('');
                    }}
                    disabled={isUnassigningTicket}
                    className={`p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 ${isUnassigningTicket ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-amber-50 dark:bg-amber-900 border border-amber-200 dark:border-amber-700 rounded-md p-4">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-100">About Unassigning</p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          When you unassign this ticket:
                        </p>
                        <ul className="text-xs text-amber-700 dark:text-amber-300 mt-2 space-y-1 ml-4 list-disc">
                          {isTeamLeadUser ? (
                            <>
                              <li>The assignee is cleared; the ticket stays in your team workflow</li>
                              <li>It remains visible under Team Tickets → Pending Assignments until someone is assigned again</li>
                              <li>It will not appear in the generic Unassigned Tickets queue while the team escalation is active</li>
                              <li>An internal note will be added for tracking purposes</li>
                              <li>You will be redirected to Team Tickets</li>
                            </>
                          ) : (
                            <>
                              <li>The ticket will be returned to the queue as &quot;Unassigned&quot;</li>
                              <li>Other agents from the correct department can pick it up</li>
                              <li>An internal note will be added for tracking purposes</li>
                              <li>You will be redirected to the unassigned tickets page</li>
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Reason for Unassigning *
                    </label>
                    <textarea
                      value={unassignReason}
                      onChange={(e) => setUnassignReason(e.target.value)}
                      placeholder="E.g., 'This ticket belongs to Network team, not my department' or 'Requires specialized expertise from Security team'..."
                      disabled={isViewMode}
                      className={`w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-orange-500 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                      rows={4}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      This reason will be added as an internal note to help the next agent understand the context
                    </p>
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-md p-3">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          <strong>Current Assignment:</strong> {ticketDetail.assignedAgent?.name || userDetails.fullName || ticketDetail.assignedTo || 'agent_1'}<br />
                          <strong>Department:</strong> {ticketDetail.department || userDetails.departmentName || 'IT'}<br />
                          <strong>Category:</strong> {ticketDetail.category || userDetails.category || 'Access'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end space-x-3 pt-4 border-t border-orange-200 dark:border-orange-700">
                    <button
                      type="button"
                      onClick={() => {
                        setShowUnassignModal(false);
                        setUnassignReason('');
                      }}
                      disabled={isViewMode || isUnassigningTicket}
                      className={`px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md ${isViewMode || isUnassigningTicket ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleUnassignTicket}
                      disabled={isViewMode || isUnassigningTicket}
                      className={`px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 flex items-center space-x-2 font-medium min-w-[14rem] justify-center ${isViewMode || isUnassigningTicket ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isUnassigningTicket ? (
                        <Loader2 className="h-5 w-5 shrink-0 animate-spin" />
                      ) : (
                        <UserMinus className="h-5 w-5 shrink-0" />
                      )}
                      <span>
                        {isUnassigningTicket
                          ? 'Submitting…'
                          : isTeamLeadUser
                            ? 'Unassign & return to Team Tickets'
                            : 'Unassign & Return to Unassigned'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showAssignInquiryModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Assign Inquiry</h3>
                    <button
                      type="button"
                      onClick={() => {
                        if (isAssigningInquiry) return;
                        setShowAssignInquiryModal(false);
                      }}
                      className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-600 dark:bg-gray-900/40 space-y-1">
                      <div><span className="text-gray-500">Inquiry Category:</span> {ticketDetail.inquiryCategoryName || '—'}</div>
                      <div><span className="text-gray-500">Inquiry Sub Category:</span> {ticketDetail.inquirySubCategoryName || '—'}</div>
                      <div><span className="text-gray-500">Escalation Level:</span> {assignInquiryEscalationLevelName || ticketDetail.escalationLevelName || 'Level 1'}</div>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Inquiry Priority Level <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={selectedInquiryAssignPriorityId}
                        onChange={(e) => handleInquiryAssignPriorityChange(e.target.value)}
                        disabled={isAssigningInquiry}
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      >
                        <option value="">Select inquiry priority...</option>
                        {inquiryAssignPriorityLevels.map((priority) => {
                          const pid = priority.id || priority.inquiryPriorityLevelId;
                          return (
                            <option key={pid} value={String(pid)}>
                              {priority.priorityLevelName || priority.name}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Responsible Role
                      </label>
                      <input
                        type="text"
                        readOnly
                        value={assignInquiryRoleName || (selectedInquiryAssignPriorityId ? '—' : 'Select Inquiry Priority first')}
                        className="w-full cursor-not-allowed rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        User <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={selectedInquiryAssigneeId}
                        onChange={(e) => setSelectedInquiryAssigneeId(e.target.value)}
                        disabled={loadingInquiryAssignUsers || !assignInquiryRoleId || isAssigningInquiry}
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      >
                        <option value="">
                          {loadingInquiryAssignUsers
                            ? 'Loading users...'
                            : !selectedInquiryAssignPriorityId
                              ? 'Select Inquiry Priority first'
                              : 'Select user...'}
                        </option>
                        {assignInquiryUsers.map((user) => {
                          const uid = user.userId || user.id;
                          const name = user.fullName || user.username || user.email || `User ${uid}`;
                          return (
                            <option key={uid} value={String(uid)}>
                              {name}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Assignment Remarks <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={inquiryAssignmentRemarks}
                        onChange={(e) => setInquiryAssignmentRemarks(e.target.value)}
                        rows={3}
                        disabled={isAssigningInquiry}
                        placeholder="Enter assignment remarks..."
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      />
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => setShowAssignInquiryModal(false)}
                        disabled={isAssigningInquiry}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-300"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAssignInquiry}
                        disabled={
                          isAssigningInquiry
                          || loadingInquiryAssignUsers
                          || !selectedInquiryAssignPriorityId
                          || !selectedInquiryAssigneeId
                          || !inquiryAssignmentRemarks.trim()
                        }
                        className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isAssigningInquiry ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <UserPlus className="h-4 w-4" />
                        )}
                        {isAssigningInquiry ? 'Assigning…' : 'Assign Inquiry'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showSupervisorRejectModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Reject Resolution
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        if (isSubmittingSupervisorAction) return;
                        setShowSupervisorRejectModal(false);
                        setSupervisorRejectError('');
                      }}
                      className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                    The Inquiry returns to the same Responsible User in In Progress. Remarks are required.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Rejection Remarks <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={supervisorRejectRemarks}
                        onChange={(e) => {
                          setSupervisorRejectRemarks(e.target.value);
                          if (supervisorRejectError) setSupervisorRejectError('');
                        }}
                        rows={4}
                        disabled={isSubmittingSupervisorAction}
                        placeholder="Explain why the resolution is rejected..."
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      />
                      {supervisorRejectError && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{supervisorRejectError}</p>
                      )}
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        type="button"
                        disabled={isSubmittingSupervisorAction}
                        onClick={() => {
                          setShowSupervisorRejectModal(false);
                          setSupervisorRejectError('');
                        }}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-300"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSupervisorReject}
                        disabled={isSubmittingSupervisorAction || supervisorRejectRemarks.trim().length < 3}
                        className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isSubmittingSupervisorAction ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <XCircle className="h-4 w-4" />
                        )}
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showRequestInfoModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Request Additional Information
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        if (isSubmittingRequestInfo) return;
                        setShowRequestInfoModal(false);
                        setRequestInfoError('');
                      }}
                      className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                    Your remarks will appear in the Inquiry conversation for the requester. Optional files upload with the request.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Remarks <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={requestInfoRemarks}
                        onChange={(e) => {
                          setRequestInfoRemarks(e.target.value);
                          if (requestInfoError) setRequestInfoError('');
                        }}
                        rows={4}
                        disabled={isSubmittingRequestInfo}
                        placeholder="Describe what additional information is needed..."
                        className={`w-full rounded-md border px-3 py-2 text-gray-900 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50 ${
                          requestInfoError
                            ? 'border-red-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      />
                      {requestInfoError && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{requestInfoError}</p>
                      )}
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Attachments (Optional)
                      </label>
                      <input
                        type="file"
                        multiple
                        onChange={(e) => setRequestInfoFiles(Array.from(e.target.files || []))}
                        disabled={isSubmittingRequestInfo}
                        className="w-full rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50"
                      />
                      {requestInfoFiles.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {requestInfoFiles.map((file, idx) => (
                            <li key={`${file.name}-${idx}`} className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                              <span className="flex min-w-0 items-center gap-2">
                                <Paperclip className="h-4 w-4 shrink-0" />
                                <span className="truncate">{file.name}</span>
                              </span>
                              <button
                                type="button"
                                disabled={isSubmittingRequestInfo}
                                onClick={() => setRequestInfoFiles((prev) => prev.filter((_, i) => i !== idx))}
                                className="text-red-600 hover:text-red-800 disabled:opacity-50"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        type="button"
                        disabled={isSubmittingRequestInfo}
                        onClick={() => {
                          setShowRequestInfoModal(false);
                          setRequestInfoError('');
                          setRequestInfoRemarks('');
                          setRequestInfoFiles([]);
                        }}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 dark:border-gray-600 dark:text-gray-400"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleRequestAdditionalInfo}
                        disabled={isSubmittingRequestInfo || !requestInfoRemarks.trim()}
                        className="flex min-w-[10rem] items-center justify-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isSubmittingRequestInfo ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Submitting…
                          </>
                        ) : (
                          <>
                            <MessageSquare className="h-4 w-4" />
                            Submit Request
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Customer Feedback Section - Only show for closed tickets */}
            {(() => {
              // Check status in multiple ways
              const currentStatus = ticketData?.status?.toLowerCase() ||
                ticketData?.statusCode?.toLowerCase() ||
                ticketData?.status?.statusCode?.toLowerCase() ||
                ticketData?.status?.statusName?.toLowerCase() || '';
              return currentStatus === 'closed';
            })() && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2 mb-4">
                    <Star className="h-5 w-5 text-yellow-500" />
                    <span>Customer Feedback</span>
                  </h2>
                  {loadingFeedback ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Loading feedback...</p>
                    </div>
                  ) : ticketFeedback ? (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="flex items-center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`h-5 w-5 ${star <= (ticketFeedback.starsCount || 0)
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-300 dark:text-gray-600'
                                }`}
                            />
                          ))}
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {ticketFeedback.starsCount || 0} out of 5
                        </span>
                      </div>
                      {ticketFeedback.comment && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 whitespace-pre-wrap">
                          {ticketFeedback.comment}
                        </p>
                      )}
                      {ticketFeedback.createdOn && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Submitted on {new Date(ticketFeedback.createdOn).toLocaleString()}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                      <p className="text-sm text-gray-600 dark:text-gray-400">No feedback submitted yet.</p>
                    </div>
                  )}
                </div>
              )}

            {/* Communication History / Replies — list scrolls; header + compose forms stay fixed */}
            <div className="min-w-0 flex flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex shrink-0 items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Replies & Communication</h2>
                <div className="flex items-center space-x-2">
                  {/* Reopen Button - Only show when status is closed */}
                  {/* {ticketData?.status?.toLowerCase() === 'closed' && (
                    <button
                      onClick={async () => {
                        try {
                          const reopenStatusId = 9; // Reopen status ID
                          await handleStatusChange(reopenStatusId);
                          toast.success('Ticket reopened successfully');
                        } catch (error) {
                          console.error('Error reopening ticket:', error);
                          toast.error(extractErrorMessage(error, 'Failed to reopen ticket'));
                        }
                      }}
                      disabled={isViewMode}
                      className={`px-3 py-1 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center space-x-2 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <RotateCcw className="h-4 w-4" />
                      <span>Reopen</span>
                    </button>
                  )} */}
                  <button
                    onClick={() => {
                      setIsAddingNote(true);
                      setIsInternalNote(true);
                      setIsAddingReply(false);
                    }}
                    disabled={isViewMode}
                    className={`px-3 py-1 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 flex items-center space-x-2 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <FileText className="h-4 w-4" />
                    <span>Add Internal Note</span>
                  </button>
                  {/* <button
                    onClick={() => {
                      setIsAddingReply(!isAddingReply);
                      setIsAddingNote(false);
                      setReplyFormError('');
                    }}
                    disabled={isViewMode}
                    className={`px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <MessageSquare className="h-4 w-4" />
                    <span>Add Reply</span>
                  </button> */}
                </div>
              </div>

              {/* Add Reply Form */}
              {isAddingReply && (
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-blue-200 dark:border-blue-800">
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Reply Content
                    </label>
                    <textarea
                      value={newReply}
                      onChange={(e) => {
                        setNewReply(e.target.value);
                        if (replyFormError) {
                          setReplyFormError('');
                        }
                      }}
                      placeholder="Enter your reply..."
                      disabled={isViewMode || isSubmittingReply}
                      aria-invalid={replyFormError ? 'true' : 'false'}
                      aria-describedby={replyFormError ? 'reply-content-error' : undefined}
                      className={`w-full p-3 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
                        replyFormError
                          ? 'border-red-500 dark:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      } ${isViewMode || isSubmittingReply ? 'opacity-50 cursor-not-allowed' : ''}`}
                      rows={4}
                    />
                    {replyFormError && (
                      <p id="reply-content-error" className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {replyFormError}
                      </p>
                    )}
                  </div>
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Attachments (Optional)
                    </label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => setReplyFiles(Array.from(e.target.files))}
                      disabled={isViewMode || isSubmittingReply}
                      className={`w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${isViewMode || isSubmittingReply ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    {replyFiles.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {replyFiles.map((file, idx) => (
                          <div key={idx} className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-2">
                            <Paperclip className="h-4 w-4" />
                            <span>{file.name}</span>
                            <button
                              onClick={() => setReplyFiles(prev => prev.filter((_, i) => i !== idx))}
                              disabled={isViewMode}
                              className={`text-red-600 hover:text-red-800 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => {
                        setIsAddingReply(false);
                        setNewReply('');
                        setReplyFormError('');
                      }}
                      disabled={isViewMode || isSubmittingReply}
                      className={`px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md ${isViewMode || isSubmittingReply ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddReply}
                      disabled={isViewMode || isSubmittingReply}
                      className={`px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2 min-w-[8.5rem] justify-center ${isViewMode || isSubmittingReply ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isSubmittingReply ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <MessageSquare className="h-4 w-4 shrink-0" />
                          <span>Add Reply</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {isAddingNote && (
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-blue-200 dark:border-blue-800">
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Note Content
                    </label>
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note or update to the ticket..."
                      disabled={isViewMode}
                      className={`w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                      rows={4}
                    />
                  </div>
                  <div className="flex items-center space-x-4 mb-3">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isInternalNote}
                        onChange={(e) => setIsInternalNote(e.target.checked)}
                        disabled={isViewMode}
                        className={`w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Internal Note (Visible to agents only)
                      </span>
                    </label>
                  </div>
                  {isInternalNote && (
                    <div className="mb-3 p-2 bg-amber-50 dark:bg-amber-900 border border-amber-200 dark:border-amber-700 rounded-md">
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          This note will only be visible to other agents and will not be shown to the customer.
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => {
                        setIsAddingNote(false);
                        setIsInternalNote(false);
                        setNewNote('');
                      }}
                      disabled={isViewMode}
                      className={`px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddNote}
                      disabled={isViewMode}
                      className={`px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <MessageSquare className="h-4 w-4" />
                      <span>Add Note</span>
                    </button>
                  </div>
                </div>
              )}

              {replies.length > 0 ? (
                <div
                  className="mt-1 min-h-0 max-h-[min(32rem,52vh)] overflow-y-auto overflow-x-hidden overscroll-y-contain rounded-xl border border-gray-100 dark:border-gray-700/80 bg-gray-50/40 dark:bg-gray-900/40 p-3 sm:p-4 space-y-4 shadow-inner
                [scrollbar-width:thin]
                [&::-webkit-scrollbar]:w-2
                [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 dark:[&::-webkit-scrollbar-track]:bg-gray-800
                [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600
                hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500"
                >
                  {replies.map((reply) => {
                    const isInternal = reply.isInternal === true || reply.type === 'internal' || reply.type === 'note';
                    const isUnassignment = reply.replyType === 'unassignment' || reply.type === 'unassignment';
                    const isTeamLeadAssignment = reply.replyType === 'team_lead_assignment' || reply.type === 'team_lead_assignment';
                    const isAdditionalInfoRequest =
                      reply.replyType === 'additional_info_request' || reply.type === 'additional_info_request';
                    const isAdditionalInfoResponse =
                      reply.replyType === 'additional_info_response' || reply.type === 'additional_info_response';
                    const isResolution =
                      reply.replyType === 'resolution' || reply.type === 'resolution';
                    return (
                      <div key={reply.id} className={`min-w-0 p-4 rounded-lg border-2 ${isUnassignment
                          ? 'bg-orange-50 dark:bg-orange-900 border-orange-300 dark:border-orange-700'
                          : isTeamLeadAssignment
                            ? 'bg-blue-50 dark:bg-blue-900 border-blue-300 dark:border-blue-700'
                            : isResolution
                              ? 'bg-green-50 dark:bg-green-900/40 border-green-300 dark:border-green-700'
                            : isAdditionalInfoRequest
                              ? 'bg-amber-50 dark:bg-amber-900/40 border-amber-300 dark:border-amber-700'
                              : isAdditionalInfoResponse
                                ? 'bg-teal-50 dark:bg-teal-900/40 border-teal-300 dark:border-teal-700'
                            : isInternal
                              ? 'bg-amber-50 dark:bg-amber-900 border-amber-200 dark:border-amber-700'
                              : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                        }`}>
                        <div className="flex min-w-0 items-start space-x-3">
                          <div className={`p-2 rounded-lg ${isUnassignment
                              ? 'bg-orange-100 dark:bg-orange-800'
                              : isTeamLeadAssignment
                                ? 'bg-blue-100 dark:bg-blue-800'
                                : isResolution
                                  ? 'bg-green-100 dark:bg-green-800'
                                : isAdditionalInfoRequest || isAdditionalInfoResponse
                                  ? 'bg-amber-100 dark:bg-amber-800'
                                : isInternal
                                  ? 'bg-amber-100 dark:bg-amber-800'
                                  : 'bg-white dark:bg-gray-800'
                            }`}>
                            {isUnassignment ? (
                              <UserMinus className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                            ) : isTeamLeadAssignment ? (
                              <UserPlus className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            ) : isResolution ? (
                              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                            ) : isInternal ? (
                              <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                            ) : (
                              <User className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                              <div className="flex items-center flex-wrap gap-2">
                                {isUnassignment && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200 rounded">
                                    Ticket Unassigned
                                  </span>
                                )}
                                {isTeamLeadAssignment && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded">
                                    Assigned to Team Leads
                                  </span>
                                )}
                                {isResolution && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-green-200 dark:bg-green-800 text-green-900 dark:text-green-100 rounded">
                                    Resolution
                                  </span>
                                )}
                                {isAdditionalInfoRequest && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 rounded">
                                    Additional Information Requested
                                  </span>
                                )}
                                {isAdditionalInfoResponse && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-teal-200 dark:bg-teal-800 text-teal-900 dark:text-teal-100 rounded">
                                    Additional Information Provided
                                  </span>
                                )}
                                {isInternal && !isUnassignment && !isTeamLeadAssignment && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded">
                                    Internal Note
                                  </span>
                                )}
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {reply.createdBy?.name || reply.author?.name || reply.author || 'System'}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {displayTimezone
                                    ? new Date(reply.createdAt || reply.timestamp).toLocaleString('en-GB', { timeZone: displayTimezone, hour12: false })
                                    : '…'}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                {editingReplyId === reply.id ? (
                                  <>
                                    <button
                                      onClick={() => handleEditReply(reply.id)}
                                      disabled={isViewMode}
                                      className={`p-1 text-green-600 hover:text-green-700 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      title="Save"
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingReplyId(null);
                                        setEditReplyContent('');
                                      }}
                                      disabled={isViewMode}
                                      className={`p-1 text-gray-600 hover:text-gray-700 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      title="Cancel"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => {
                                        setEditingReplyId(reply.id);
                                        setEditReplyContent(reply.content || reply.message || '');
                                      }}
                                      disabled={isViewMode}
                                      className={`p-1 text-blue-600 hover:text-blue-700 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      title="Edit"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setReplyToDelete(reply.id);
                                        setShowDeleteReplyConfirm(true);
                                      }}
                                      disabled={isViewMode}
                                      className={`p-1 text-red-600 hover:text-red-700 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      title="Delete"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            {editingReplyId === reply.id ? (
                              <textarea
                                value={editReplyContent}
                                onChange={(e) => setEditReplyContent(e.target.value)}
                                disabled={isViewMode}
                                className={`w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                                rows={4}
                              />
                            ) : (
                              <p className="mb-2 max-w-full break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100 whitespace-pre-wrap">
                                {reply.content || reply.message}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900/30 py-10 px-4 text-center">
                  <MessageSquare className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No replies yet. Be the first to reply!
                  </p>
                </div>
              )}
            </div>

            {/* Linked Tickets (V1 parent-child) — feature-flagged via /config/features.
                Sits inside the left column directly after Replies & Communication so the
                grid auto-flow stays clean (right sidebar is unaffected). */}
            {ticketRelationsEnabled && (
              <LinkedTicketsPanel
                bundle={relationsBundle}
                isLoading={relationsLoading}
                onOpenAttach={() => setShowAttachModal(true)}
                onOpenCreateChild={() => setShowCreateChildModal(true)}
                onUnlink={handleRelationUnlink}
              />
            )}

            {ticketRelationsEnabled && (
              <LinkedDuplicatesPanel
                bundle={relationsBundle}
                isLoading={relationsLoading}
                canLink={canShowDuplicateLinkAction}
                onOpenLinkDuplicate={() => setShowAttachDuplicateModal(true)}
                canCloseAsDuplicate={canCloseAsDuplicate}
                onCloseAsDuplicate={handleOpenCloseAsDuplicate}
              />
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="min-w-0 space-y-6">
            {/* SLA Status */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">SLA Status</h3>
              {(() => {
                const isTerminal = isTerminalSlaStatus(ticketData, statuses);
                const isPaused = isSlaPausedStatus(ticketData, statuses);
                const ticketStatus = ticketData?.status?.toLowerCase() || '';
                const effectiveSla = isTerminal ? mapTicketSlaFields(ticketData) : slaStatus;
                const assignedTime = ticketData?.assignedTime ?? effectiveSla?.assignedTime ?? null;

                const elapsedMinutes = resolveDisplayElapsedMinutes({
                  effectiveSla,
                  isTerminal,
                  isPaused,
                  assignedTime,
                  slaUsesBusinessHours: ticketData?.slaUsesBusinessHours === true,
                });

                const remainingMinutes = isTerminal
                  ? (effectiveSla?.remainingMinutes ?? null)
                  : (slaTimer !== null ? slaTimer : (slaStatus?.remainingMinutes ?? null));
                const remainingSeconds = isTerminal
                  ? (effectiveSla?.remainingSeconds ?? 0)
                  : (slaTimerSeconds !== null ? slaTimerSeconds : (slaStatus?.remainingSeconds ?? 0));
                const overdueMinutes = isTerminal
                  ? (effectiveSla?.overdueMinutes ?? 0)
                  : (slaOverdueMinutes !== null ? slaOverdueMinutes : (slaStatus?.overdueMinutes ?? 0));
                const overdueSeconds = isTerminal
                  ? (effectiveSla?.overdueSeconds ?? 0)
                  : (slaOverdueSeconds !== null ? slaOverdueSeconds : (slaStatus?.overdueSeconds ?? 0));
                const isOverdueTicking = !isTerminal
                  && slaOverdueMinutes !== null
                  && (slaOverdueMinutes > 0 || slaOverdueSeconds > 0);

                const slaApplicable = effectiveSla?.slaApplicable === true;
                const isEscalated = effectiveSla?.isEscalated === true;
                const hasFrozenOverdue = (overdueMinutes ?? 0) > 0 || (overdueSeconds ?? 0) > 0;
                const isBreached = slaApplicable && (
                  effectiveSla?.isBreached === true
                  || isOverdueTicking
                  || hasFrozenOverdue
                  || (!isTerminal && remainingMinutes !== null && remainingMinutes <= 0 && remainingSeconds <= 0)
                );

                let displayStatus = 'on-track';
                if (!slaApplicable) {
                  displayStatus = 'default';
                } else if (isEscalated || isBreached) {
                  displayStatus = 'breach';
                } else if (remainingMinutes !== null && remainingMinutes < 60) {
                  displayStatus = 'warning';
                }

                const SLAIcon = getSLAIcon(displayStatus);

                const getSlaPrimaryText = () => {
                  if (!effectiveSla) {
                    return isTerminal ? 'SLA status unavailable' : 'Calculating...';
                  }
                  if (isTerminal) {
                    if (isEscalated) {
                      return 'Escalated';
                    }
                    if (isBreached || hasFrozenOverdue) {
                      return formatSlaOverdue(overdueMinutes, overdueSeconds);
                    }
                    if (ticketStatus === 'resolved') {
                      return 'Resolved within SLA';
                    }
                    if (remainingMinutes != null && (remainingMinutes > 0 || remainingSeconds > 0)) {
                      return formatSlaRemaining(remainingMinutes, remainingSeconds);
                    }
                    return 'Completed within SLA';
                  }
                  if (isOverdueTicking) {
                    return formatSlaOverdue(overdueMinutes, overdueSeconds);
                  }
                  if (remainingMinutes !== null) {
                    return formatSlaRemaining(remainingMinutes, remainingSeconds);
                  }
                  return 'Calculating...';
                };

                if (effectiveSla && !slaApplicable) {
                  return (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-3">
                        <Clock className="h-6 w-6 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            No SLA configured
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            This category and priority have no active SLA policy.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }

                if (!assignedTime) {
                  return (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-3">
                        <SLAIcon className={`h-6 w-6 ${getSLAStatusColor(displayStatus)}`} />
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Not assigned yet
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {slaApplicable
                              ? 'SLA timer starts when the ticket is assigned'
                              : 'Assign the ticket to begin tracking'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <SLAIcon className={`h-6 w-6 ${getSLAStatusColor(displayStatus)}`} />
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${
                          isBreached || isEscalated
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          {getSlaPrimaryText()}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Assigned: {new Date(assignedTime).toLocaleString()}
                        </p>
                        {isPaused && slaApplicable && (
                          <p className="text-xs text-amber-600 dark:text-amber-400">
                            SLA paused — waiting on requester
                          </p>
                        )}
                        {slaApplicable && elapsedMinutes !== null && elapsedMinutes > 0 && !isBreached && !isTerminal && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Elapsed: {formatTimeFromMinutes(elapsedMinutes)}
                          </p>
                        )}
                      </div>
                    </div>
                    {isEscalated && (
                      <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                        <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                          ⚠️ Ticket has been auto-escalated due to SLA breach
                        </p>
                      </div>
                    )}
                    {isBreached && !isEscalated && !isTerminal && (
                      <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md">
                        <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                          ⚠️ SLA breach detected - ticket should be escalated
                        </p>
                      </div>
                    )}
                    {isBreached && !isEscalated && isTerminal && (
                      <div className="mt-2 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md">
                        <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                          ⚠️ SLA was breached before ticket closure
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Attachments */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Attachments</h3>
                {!isViewMode && (
                  <>
                    <label
                      htmlFor="attachment-file-input"
                      className={`px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2 ${(uploadingAttachment || uploadJustCompleted) ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer'
                        }`}
                      onClick={(e) => {
                        if (uploadingAttachment || uploadJustCompleted) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                    >
                      <Plus className="h-4 w-4" />
                      <span>Upload</span>
                    </label>
                    <input
                      id="attachment-file-input"
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setSelectedFile(file);
                          setUploadJustCompleted(false); // Re-enable upload button when new file is selected
                        }
                      }}
                      multiple={false}
                      disabled={isViewMode || uploadingAttachment || uploadJustCompleted}
                    />
                  </>
                )}
              </div>

              {/* File Upload UI */}
              {selectedFile && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Paperclip className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-900 dark:text-gray-100">{selectedFile.name}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">({formatFileSize(selectedFile.size)})</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={handleUploadAttachment}
                        disabled={uploadingAttachment || isViewMode}
                        className={`px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 ${isViewMode ? 'cursor-not-allowed' : ''}`}
                      >
                        {uploadingAttachment ? 'Uploading...' : 'Upload'}
                      </button>
                      <button
                        onClick={() => setSelectedFile(null)}
                        disabled={isViewMode}
                        className={`p-1 text-gray-400 hover:text-gray-600 ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {attachments.length > 0 ? (
                  attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-start justify-between gap-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex gap-2 flex-1 min-w-0">
                        <Paperclip className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" aria-hidden />
                        <div className="min-w-0 flex-1 space-y-0.5">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {attachment.name || attachment.fileName}
                          </p>
                          {(attachment.uploaderName || attachment.uploaderRole) && (
                            <p className="text-xs text-gray-600 dark:text-gray-300 break-words">
                              Uploaded by:{' '}
                              <span className="font-medium text-gray-800 dark:text-gray-100">
                                {attachment.uploaderName || 'Unknown'}
                                {attachment.uploaderRole ? ` (${attachment.uploaderRole})` : ''}
                              </span>
                            </p>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatTicketFileUploadedAt(attachment.createdOn || attachment.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 flex-shrink-0 pt-0.5">
                        <button
                          onClick={() => handleDownloadAttachment(attachment)}
                          className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-300"
                          title={`Download ${attachment.name || attachment.fileName}`}
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        {/* {!isViewMode && (
                        <button 
                          onClick={() => {
                            setAttachmentToDelete(attachment.id);
                            setShowDeleteAttachmentConfirm(true);
                          }}
                          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-300"
                          title={`Delete ${attachment.name || attachment.fileName}`}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        )} */}
                        {/* {!isViewMode && ( */}
                        <button
                          onClick={() => {
                            // Use documentPath from attachment or fallback to url
                            const documentPath = attachment.documentPath || attachment.document_path;
                            if (documentPath) {
                              const DOMAIN_URL = window.location.hostname === 'localhost' ? 'https://helpdeskdev.dhanushinfotech.com' : window.location.origin;
                              const fullUrl = `${DOMAIN_URL}${documentPath.startsWith('/') ? '' : '/'}${documentPath}`;
                              window.open(fullUrl, "_blank");
                            } else if (attachment.url && attachment.url !== '#') {
                              // Fallback to url if documentPath is not available
                              window.open(attachment.url, "_blank");
                            } else {
                              toast.error('File path not available');
                            }
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-300"
                          title={`View ${attachment.name || attachment.fileName}`}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {/* )} */}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    No attachments
                  </p>
                )}
              </div>
            </div>

            {/* Time Tracking */}

            {/* Quick Actions */}
            {showActions && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  {ticketActions.map((action) => (
                    <button
                      key={action.id}
                      onClick={() => handleAction(action.id)}
                      className="w-full flex items-center space-x-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                    >
                      <div className={`p-2 rounded-lg ${action.color === 'green' ? 'bg-green-100 dark:bg-green-900' :
                          action.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900' :
                            action.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900' :
                              action.color === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900' :
                                action.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900' :
                                  action.color === 'indigo' ? 'bg-indigo-100 dark:bg-indigo-900' :
                                    'bg-gray-100 dark:bg-gray-700'
                        }`}>
                        {action.icon === 'check-circle' && <CheckCircle className="h-4 w-4" />}
                        {action.icon === 'arrow-up' && <ArrowUp className="h-4 w-4" />}
                        {action.icon === 'x-circle' && <XCircle className="h-4 w-4" />}
                        {action.icon === 'user-check' && <UserCheck className="h-4 w-4" />}
                        {action.icon === 'flag' && <Flag className="h-4 w-4" />}
                        {action.icon === 'message-square' && <MessageSquare className="h-4 w-4" />}
                        {action.icon === 'paperclip' && <Paperclip className="h-4 w-4" />}
                        {action.icon === 'play' && <Play className="h-4 w-4" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{action.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{action.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Summary Modal */}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 p-6 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">AI Communication Summary</h2>
                    <p className="text-sm text-purple-100">Intelligent analysis of ticket history</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSummaryModal(false)}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {isSummarizing ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center space-x-2 mb-4">
                    <Sparkles className="h-8 w-8 text-purple-600 animate-pulse" />
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 font-medium">Analyzing communication history...</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">AI is processing the conversation</p>
                </div>
              ) : summary ? (
                <div className="space-y-6">
                  {/* Summary API Message */}
                  {summaryApiMessage && (
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900 dark:to-purple-900 rounded-lg p-4 border border-indigo-200 dark:border-indigo-700">
                      <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-100 mb-1">
                        Summary API Message
                      </h3>
                      <p className="text-sm text-indigo-800 dark:text-indigo-200 whitespace-pre-wrap">
                        {summaryApiMessage}
                      </p>
                    </div>
                  )}

                  {/* Overview */}
                  <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900 dark:to-indigo-900 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                    <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-2 flex items-center">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Overview
                    </h3>
                    <p className="text-sm text-purple-800 dark:text-purple-200 whitespace-pre-wrap">{summary.overview}</p>
                  </div>

                  {/* Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                      <p className="text-xs text-blue-600 dark:text-blue-300 font-medium">Total Messages</p>
                      <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{summary.statistics.totalMessages}</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900 rounded-lg p-4 border border-green-200 dark:border-green-700">
                      <p className="text-xs text-green-600 dark:text-green-300 font-medium">Comments</p>
                      <p className="text-2xl font-bold text-green-900 dark:text-green-100">{summary.statistics.commentCount}</p>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
                      <p className="text-xs text-amber-600 dark:text-amber-300 font-medium">Internal Notes</p>
                      <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">{summary.statistics.noteCount}</p>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                      <p className="text-xs text-purple-600 dark:text-purple-300 font-medium">Participants</p>
                      <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{summary.statistics.participantCount}</p>
                    </div>
                  </div>

                  {/* Key Points */}
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                      Key Points
                    </h3>
                    {summary.keyPoints && summary.keyPoints.length > 0 ? (
                      <ul className="space-y-2">
                        {summary.keyPoints.map((point, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300 text-xs flex items-center justify-center font-medium mt-0.5">
                              {index + 1}
                            </span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">{point}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">No key points available</p>
                    )}
                  </div>

                  {/* Participants & Sentiment */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                        <User className="h-4 w-4 mr-2 text-blue-600" />
                        Participants
                      </h3>
                      {summary.participants && summary.participants.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {summary.participants.map((participant, index) => (
                            <span key={index} className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full font-medium">
                              {participant}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">No participants available</p>
                      )}
                    </div>

                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Sentiment & Urgency</h3>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Sentiment:</span>
                          <span className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs rounded-full font-medium">
                            {summary.sentiment}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Urgency:</span>
                          <span className={`px-3 py-1 text-xs rounded-full font-medium ${summary.urgency.includes('High')
                              ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                              : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                            }`}>
                            {summary.urgency.split(' - ')[0]}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Next Steps */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900 dark:to-emerald-900 rounded-lg p-4 border border-green-200 dark:border-green-700">
                    <h3 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-3 flex items-center">
                      <ArrowUp className="h-4 w-4 mr-2" />
                      Recommended Next Steps
                    </h3>
                    <ul className="space-y-2">
                      {summary.nextSteps.map((step, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-green-800 dark:text-green-200">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Timeline */}
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-gray-600" />
                      Timeline
                    </h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{summary.timeline}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">No summary available</p>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 p-4 rounded-b-lg border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Powered by AI Analysis
                </p>
                <button
                  onClick={() => setShowSummaryModal(false)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Article Modal */}
      {showGenerateArticleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-blue-600 p-6 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">AI Generated Knowledge Article</h2>
                    <p className="text-sm text-indigo-100">Article generated from ticket information</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowGenerateArticleModal(false)}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {isGeneratingArticle ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center space-x-2 mb-4">
                    <FileText className="h-8 w-8 text-indigo-600 animate-pulse" />
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 font-medium">Generating knowledge article...</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">AI is analyzing the ticket and creating an article</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* AI Generate Article API Message */}
                  {articleApiMessage && (
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900 dark:to-blue-900 rounded-lg p-4 border border-indigo-200 dark:border-indigo-700">
                      <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-100 mb-1">
                        Generate Article API Message
                      </h3>
                      <p className="text-sm text-indigo-800 dark:text-indigo-200 whitespace-pre-wrap">
                        {articleApiMessage}
                      </p>
                    </div>
                  )}

                  {generatedArticle ? (
                    <>
                      {/* Title */}
                      <div className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900 dark:to-blue-900 rounded-lg p-4 border border-indigo-200 dark:border-indigo-700">
                        <h3 className="text-sm font-semibold text-indigo-900 dark:text-indigo-100 mb-2 flex items-center">
                          <FileText className="h-4 w-4 mr-2" />
                          Title
                        </h3>
                        <p className="text-base font-medium text-indigo-800 dark:text-indigo-200">{generatedArticle.title}</p>
                      </div>

                      {/* Question */}
                      <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                          <MessageSquare className="h-4 w-4 mr-2 text-blue-600" />
                          Question
                        </h3>
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{generatedArticle.question}</p>
                      </div>

                      {/* Answer */}
                      <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                          Answer
                        </h3>
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{generatedArticle.answer}</p>
                      </div>

                      {/* Category and Tags */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                            <Tag className="h-4 w-4 mr-2 text-purple-600" />
                            Category
                          </h3>
                          <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-sm rounded-full font-medium">
                            {generatedArticle.category || 'N/A'}
                          </span>
                        </div>

                        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                            <Tag className="h-4 w-4 mr-2 text-blue-600" />
                            Tags
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {generatedArticle.tags && generatedArticle.tags.length > 0 ? (
                              generatedArticle.tags.map((tag, index) => (
                                <span key={index} className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full font-medium">
                                  {tag}
                                </span>
                              ))
                            ) : (
                              <span className="text-sm text-gray-500 dark:text-gray-400">No tags</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Ticket ID */}
                      {generatedArticle.ticketId && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
                            <Ticket className="h-4 w-4 mr-2 text-gray-600" />
                            Related Ticket
                          </h3>
                          <p className="text-sm text-gray-700 dark:text-gray-300 font-mono">{generatedArticle.ticketId}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">No article generated yet</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 p-4 rounded-b-lg border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                  <FileText className="h-3 w-3 mr-1" />
                  Powered by AI Article Generation
                </p>
                <div className="flex items-center space-x-2">
                  {generatedArticle && (
                    <button
                      onClick={handleSaveArticle}
                      disabled={isSavingArticle}
                      className={`px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center space-x-2 ${isSavingArticle ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    >
                      {isSavingArticle ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4" />
                          <span>Save Article</span>
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => setShowGenerateArticleModal(false)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Ticket Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Edit Ticket</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={editFormData.subject || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  value={editFormData.description || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Priority
                  </label>
                  {canEditTicketPriority ? (
                    <select
                      value={editFormData.priorityId || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, priorityId: e.target.value ? Number(e.target.value) : null })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Select priority...</option>
                      {editablePriorities.map(priority => (
                        <option key={priority.priorityId} value={priority.priorityId}>
                          {priority.priorityName || priority.priorityCode || 'Unknown'}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900">
                      {(
                        isInquiryTicket
                          ? (ticketData?.inquiryPriorityLevelName || ticketData?.priorityName || ticketData?.priority)
                          : (ticketData?.priorityName || ticketData?.priority)
                        || '—'
                      ).toString()}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={editFormData.statusId || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, statusId: e.target.value ? Number(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Select status...</option>
                    {statuses.map(status => (
                      <option key={status.statusId} value={status.statusId}>
                        {status.statusName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={editFormData.categoryId || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, categoryId: e.target.value ? Number(e.target.value) : null })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Select category...</option>
                  {categories.map(category => (
                    <option key={category.categoryId || category.id} value={category.categoryId || category.id}>
                      {category.categoryName || category.name || `Category ${category.categoryId || category.id}`}
                    </option>
                  ))}
                </select>
              </div>
              {editFormData.customFieldValues &&
                typeof editFormData.customFieldValues === 'object' &&
                Object.keys(editFormData.customFieldValues).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Custom fields
                    </label>
                    <div className="space-y-3 rounded-md border border-gray-200 dark:border-gray-600 p-3 max-h-48 overflow-y-auto">
                      {Object.keys(editFormData.customFieldValues).map((fieldCode) => (
                        <div key={fieldCode}>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            {fieldCode.replace(/_/g, ' ')}
                          </label>
                          <input
                            type="text"
                            value={
                              editFormData.customFieldValues[fieldCode] != null
                                ? String(editFormData.customFieldValues[fieldCode])
                                : ''
                            }
                            onChange={(e) =>
                              setEditFormData((prev) => ({
                                ...prev,
                                customFieldValues: {
                                  ...(prev.customFieldValues || {}),
                                  [fieldCode]: e.target.value,
                                },
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTicket}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Ticket Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Delete Ticket</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this ticket? This is a soft delete and can be reversed. The ticket will be marked as deleted.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTicket}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete Ticket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Reply Confirmation Modal */}
      {showDeleteReplyConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Delete Reply</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this reply? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteReplyConfirm(false);
                  setReplyToDelete(null);
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteReply}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Attachment Confirmation Modal */}
      {showDeleteAttachmentConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Delete Attachment</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this attachment? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteAttachmentConfirm(false);
                  setAttachmentToDelete(null);
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAttachment}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Parent-child relation modals — only mounted when the V1 feature flag is on
          AND the user has explicitly opened them. They are intentionally outside the
          main layout so they overlay the page without disturbing the grid. */}
      {ticketRelationsEnabled && (
        <>
          <AttachExistingTicketModal
            parentTicketId={ticketData?.ticketId || ticketData?.id}
            parentTicketNumber={ticketData?.ticketNumber}
            isOpen={showAttachModal}
            onClose={() => setShowAttachModal(false)}
            onAttached={() => fetchRelationsBundle()}
          />
          {canShowDuplicateLinkAction && (
          <AttachDuplicateModal
            currentTicketId={ticketData?.ticketId || ticketData?.id}
            currentTicketNumber={ticketData?.ticketNumber}
            linkMode={duplicateLinkMode}
            isOpen={showAttachDuplicateModal}
            onClose={() => setShowAttachDuplicateModal(false)}
            onLinked={handleDuplicateLinked}
          />
          )}
          <CreateChildTicketModal
            parentTicket={ticketData ? {
              ticketId: ticketData.ticketId || ticketData.id,
              ticketNumber: ticketData.ticketNumber,
              categoryId: ticketData.categoryId,
              priorityId: ticketData.priorityId,
              ticketTypeId: ticketData.ticketTypeId,
              departmentId: ticketData.departmentId,
              teamId: ticketData.teamId,
            } : null}
            isOpen={showCreateChildModal}
            onClose={() => setShowCreateChildModal(false)}
            onCreated={() => fetchRelationsBundle()}
          />
          <CloseAsDuplicateModal
            isOpen={showCloseAsDuplicateModal && Boolean(relationsBundle?.duplicateOf)}
            onClose={() => {
              if (!closingAsDuplicate) {
                setShowCloseAsDuplicateModal(false);
                setCloseAsDuplicateError('');
              }
            }}
            currentTicketNumber={ticketData?.ticketNumber}
            canonicalTicketNumber={
              relationsBundle?.duplicateOf?.ticketNumber
              || (relationsBundle?.duplicateOf?.ticketId
                ? `#${relationsBundle.duplicateOf.ticketId}`
                : undefined)
            }
            reason={closeAsDuplicateReason}
            onReasonChange={(value) => {
              setCloseAsDuplicateReason(value);
              if (closeAsDuplicateError) setCloseAsDuplicateError('');
            }}
            error={closeAsDuplicateError}
            submitting={closingAsDuplicate}
            onSubmit={handleCloseAsDuplicate}
          />
        </>
      )}
    </div>
  );
};

export default TicketDetail;
